<script>
  import { onMount } from "svelte";
  import { slide, fade } from "svelte/transition";
  import {
    customerQuote,
    loadAddressText,
    loadAddresCoordinate,
    yards,
    quoteSelectedMaterial,
    routeTravelEstimates,
    activeTab,
    selectedQuoteForJob,
    selectedJobForEdit,
    showCustomerQuoteWindow,
  } from "../../stores.js";
  import {
    fetchQuotes,
    saveQuote,
    updateQuoteDoc,
    deleteQuote,
    fetchCompanies,
    saveCompany,
    generateJobFromQuote,
    fetchYards,
  } from "../../data.js";
  import {
    fetchAndLoadQuote,
    loadQuoteIntoCustomerQuoteStore,
  } from "../../quoteUtils.js";
  import { formatPhoneNumber, toE164 } from "../../phoneUtils.js";
  import {
    calculateFleetDaysAndCosts,
    getFullAndRemainderDays,
  } from "../Quotes/fleetScheduleCalculator.js";
  import { generateQuotePdf } from "../Quotes/quotePdfGenerator.js";
  import { reportYardImageError, inlineAddress, portal, isFullAddress, validateFullAddress, parseMapboxAddress } from "../../helpers.js";
  import { Geocoder } from "@beyonk/svelte-mapbox";

  import PhoneInput from "../../components/PhoneInput.svelte";
  import CompanyForm from "../../forms/Company/CompanyForm.svelte";

  // Carbon icons
  import Search from "carbon-icons-svelte/lib/Search.svelte";
  import DocumentAdd from "carbon-icons-svelte/lib/DocumentAdd.svelte";
  import Add from "carbon-icons-svelte/lib/Add.svelte";
  import Calculator from "carbon-icons-svelte/lib/Calculator.svelte";
  import Save from "carbon-icons-svelte/lib/Save.svelte";
  import Printer from "carbon-icons-svelte/lib/Printer.svelte";
  import Copy from "carbon-icons-svelte/lib/Copy.svelte";
  import Checkmark from "carbon-icons-svelte/lib/Checkmark.svelte";
  import WarningFilled from "carbon-icons-svelte/lib/WarningFilled.svelte";
  import ChevronLeft from "carbon-icons-svelte/lib/ChevronLeft.svelte";
  import ChevronRight from "carbon-icons-svelte/lib/ChevronRight.svelte";
  import ChevronUp from "carbon-icons-svelte/lib/ChevronUp.svelte";
  import ChevronDown from "carbon-icons-svelte/lib/ChevronDown.svelte";
  import DeliveryTruck from "carbon-icons-svelte/lib/DeliveryTruck.svelte";
  import Edit from "carbon-icons-svelte/lib/Edit.svelte";
  import Close from "carbon-icons-svelte/lib/Close.svelte";
  import User from "carbon-icons-svelte/lib/User.svelte";
  import Location from "carbon-icons-svelte/lib/Location.svelte";
  import Document from "carbon-icons-svelte/lib/Document.svelte";
  import TrashCan from "carbon-icons-svelte/lib/TrashCan.svelte";
  import Filter from "carbon-icons-svelte/lib/Filter.svelte";
  import Enterprise from "carbon-icons-svelte/lib/Enterprise.svelte";
  import ArrowRight from "carbon-icons-svelte/lib/ArrowRight.svelte";
  import Phone from "carbon-icons-svelte/lib/Phone.svelte";
  import Email from "carbon-icons-svelte/lib/Email.svelte";
  import Renew from "carbon-icons-svelte/lib/Renew.svelte";
  import Receipt from "carbon-icons-svelte/lib/Receipt.svelte";

  // State
  let quotes = [];
  let isLoadingQuotes = false;
  let quotesError = "";
  let searchQuery = "";
  let isSidebarOpen = true;

  // Status filter multi-select: Default to Draft, Sent, and Accepted
  let selectedStatuses = ["draft", "sent", "accepted"];
  let isStatusDropdownOpen = false;
  let multiselectContainer = null;

  const STATUS_OPTIONS = [
    { value: "draft", label: "Draft", color: "#475569", bg: "#f1f5f9", border: "#cbd5e1" },
    { value: "sent", label: "Sent", color: "#0369a1", bg: "#e0f2fe", border: "#bae6fd" },
    { value: "accepted", label: "Accepted", color: "#15803d", bg: "#dcfce7", border: "#bbf7d0" },
    { value: "declined", label: "Declined", color: "#b91c1c", bg: "#fee2e2", border: "#fecaca" },
    { value: "converted", label: "Converted", color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" }
  ];

  function toggleStatusFilter(statusVal) {
    if (selectedStatuses.includes(statusVal)) {
      selectedStatuses = selectedStatuses.filter((s) => s !== statusVal);
    } else {
      selectedStatuses = [...selectedStatuses, statusVal];
    }
  }

  function resetStatusToDefault() {
    selectedStatuses = ["draft", "sent", "accepted"];
  }

  function selectAllStatuses() {
    selectedStatuses = STATUS_OPTIONS.map((opt) => opt.value);
  }

  function clearAllStatuses() {
    selectedStatuses = [];
  }

  function handleClickOutside(event) {
    if (isStatusDropdownOpen && multiselectContainer && !multiselectContainer.contains(event.target)) {
      isStatusDropdownOpen = false;
    }
  }

  function handleWindowKeydown(event) {
    if (event.key === "Escape" && isStatusDropdownOpen) {
      isStatusDropdownOpen = false;
    }
  }

  // Feedback banners
  let copyFeedback = false;
  let copyFeedbackTimeout = null;
  let pdfFeedback = false;
  let pdfFeedbackTimeout = null;
  let isSavingQuote = false;
  let isCreatingJob = false;
  let saveFeedbackMessage = "";
  let saveFeedbackType = "";
  let saveFeedbackTimeout = null;

  // Company management state
  let companies = [];
  let isLoadingCompanies = false;
  let companySearchTerm = "";
  let isCompanySearchFocused = false;
  let selectedCompany = null;
  let showEditCompanyModal = false;

  // Initialize selectedCompany from customerQuote store if already present
  if ($customerQuote?.selectedCompany) {
    selectedCompany = $customerQuote.selectedCompany;
  } else if ($customerQuote?.customerName && $customerQuote.customerName.trim()) {
    selectedCompany = {
      name: $customerQuote.customerName.trim(),
      phone: $customerQuote.companyPhone || $customerQuote.phone || "",
      address: $customerQuote.companyAddress || null,
      contacts: []
    };
  }

  $: isCompanySelected = Boolean(
    (selectedCompany && (selectedCompany.name?.trim() || selectedCompany.id)) ||
    ($customerQuote?.selectedCompany && ($customerQuote.selectedCompany.name?.trim() || $customerQuote.selectedCompany.id)) ||
    ($customerQuote?.customerName && $customerQuote.customerName.trim().length > 0 && selectedCompany)
  );

  // Prepared For (contact person) state
  let preparedForInput = "";
  let isPreparedForFocused = false;
  let showSaveContactModal = false;
  let newContactData = {
    name: "",
    position: "",
    phone: "",
    email: "",
  };
  let blurTimeout = null;

  // Delete Quote modal state & functions
  let isDeleteQuoteModalOpen = false;
  let isDeletingQuote = false;
  let deleteQuoteError = "";
  let deleteQuoteSuccessNotice = "";

  async function handleConfirmDeleteQuote() {
    const quoteId = $customerQuote.id;
    if (!quoteId) {
      handleNewQuote();
      isDeleteQuoteModalOpen = false;
      deleteQuoteSuccessNotice = "Unsaved quote draft discarded.";
      setTimeout(() => (deleteQuoteSuccessNotice = ""), 4000);
      return;
    }

    isDeletingQuote = true;
    deleteQuoteError = "";
    try {
      await deleteQuote(quoteId);
      deleteQuoteSuccessNotice = `Quote #${$customerQuote.quoteNumber || quoteId} was permanently deleted.`;
      isDeleteQuoteModalOpen = false;

      await loadQuotes();

      if (quotes.length > 0) {
        const nextQuote = quotes.find((q) => (q.id || q._id) !== quoteId) || quotes[0];
        if (nextQuote && (nextQuote.id || nextQuote._id) !== quoteId) {
          handleSelectQuote(nextQuote);
        } else {
          handleNewQuote();
        }
      } else {
        handleNewQuote();
      }

      setTimeout(() => {
        deleteQuoteSuccessNotice = "";
      }, 5000);
    } catch (err) {
      console.error("[handleConfirmDeleteQuote] Error:", err);
      deleteQuoteError = err.message || "Failed to delete quote.";
    } finally {
      isDeletingQuote = false;
    }
  }

  onMount(async () => {
    if (!$yards || $yards.length === 0) {
      try {
        await fetchYards();
      } catch (e) {
        console.warn("[QuoteDetails] Failed to load yards:", e);
      }
    }
    await Promise.all([loadQuotes(), loadCompaniesList()]);
  });

  async function loadQuotes() {
    isLoadingQuotes = true;
    quotesError = "";
    try {
      const data = await fetchQuotes();
      const list = Array.isArray(data) ? data : data?.quotes || [];
      quotes = list.sort((a, b) => {
        const idA = Number(a.id || a._id || 0);
        const idB = Number(b.id || b._id || 0);
        return idB - idA;
      });
    } catch (err) {
      console.error("[QuoteDetails] Failed to load quotes:", err);
      quotesError = err.message || "Failed to load quotes.";
    } finally {
      isLoadingQuotes = false;
    }
  }

  async function loadCompaniesList() {
    try {
      isLoadingCompanies = true;
      const res = await fetchCompanies();
      companies = Array.isArray(res) ? res : res?.companies || [];
      if (!selectedCompany) {
        if ($customerQuote.selectedCompany) {
          selectedCompany = $customerQuote.selectedCompany;
        } else if ($customerQuote.customerName) {
          matchCompanyByName($customerQuote.customerName);
        }
      }
    } catch (err) {
      console.error("[QuoteDetails] Failed to fetch companies:", err);
    } finally {
      isLoadingCompanies = false;
    }
  }

  function matchCompanyByName(name) {
    if (!name) return;
    const match = companies.find(
      (c) => c.name && c.name.trim().toLowerCase() === name.trim().toLowerCase(),
    );
    if (match) {
      selectedCompany = match;
      if (!selectedCompany.contacts)
        selectedCompany.contacts = match.people || [];
      $customerQuote.selectedCompany = match;
    } else {
      selectedCompany = {
        name: name,
        phone: $customerQuote.companyPhone || $customerQuote.phone || "",
        address: $customerQuote.companyAddress || null,
        contacts: [],
      };
    }
  }

  $: if (
    $customerQuote.customerName &&
    !selectedCompany &&
    companies.length > 0
  ) {
    matchCompanyByName($customerQuote.customerName);
  }

  $: if (
    $customerQuote.selectedCompany &&
    (!selectedCompany ||
      selectedCompany.id !== $customerQuote.selectedCompany.id)
  ) {
    selectedCompany = $customerQuote.selectedCompany;
  }

  $: filteredCompanies = (companies || []).filter((c) => {
    if (!companySearchTerm.trim()) return false;
    const term = companySearchTerm.toLowerCase();
    const nameMatch = c.name && c.name.toLowerCase().includes(term);
    const abnMatch = c.ABN && String(c.ABN).toLowerCase().includes(term);
    const phoneMatch = c.phone && String(c.phone).toLowerCase().includes(term);
    const suburbMatch =
      c.address?.suburb && c.address.suburb.toLowerCase().includes(term);
    return nameMatch || abnMatch || phoneMatch || suburbMatch;
  });

  function selectCompany(comp) {
    selectedCompany = comp;
    if (!selectedCompany.contacts) {
      selectedCompany.contacts = comp.people || [];
    }
    $customerQuote.customerName = comp.name || "";
    $customerQuote.selectedCompany = comp;
    $customerQuote.companyAddress = comp.address || null;
    $customerQuote.companyPhone = comp.phone || "";
    companySearchTerm = "";
    isCompanySearchFocused = false;

    if (
      !$customerQuote.contactPerson &&
      Array.isArray(selectedCompany.contacts) &&
      selectedCompany.contacts.length > 0
    ) {
      const mainContact =
        selectedCompany.contacts.find((c) => c.main) ||
        selectedCompany.contacts[0];
      if (mainContact) {
        selectContact(mainContact);
      }
    }
  }

  function clearSelectedCompany() {
    selectedCompany = null;
    companySearchTerm = "";
    isCompanySearchFocused = false;
    preparedForInput = "";
    $customerQuote.customerName = "";
    $customerQuote.contactPerson = "";
    $customerQuote.prepared_for = "";
    $customerQuote.selectedCompany = null;
    $customerQuote.companyAddress = null;
    $customerQuote.companyPhone = "";
    $customerQuote.phone = "";
    $customerQuote.email = "";
    $customerQuote.position = "";
    isPreparedForFocused = false;
  }

  function handleCreateNewCompany(name) {
    selectedCompany = {
      name: name,
      phone: "",
      address: {},
      ABN: "",
      ACN: "",
      contacts: [],
    };
    $customerQuote.customerName = name;
    $customerQuote.selectedCompany = selectedCompany;
    companySearchTerm = "";
    isCompanySearchFocused = false;
    showEditCompanyModal = true;
  }

  function handleCompanySaved(e) {
    if (e.detail) {
      selectedCompany = e.detail;
      if (!selectedCompany.contacts)
        selectedCompany.contacts = selectedCompany.people || [];
      $customerQuote.customerName = selectedCompany.name;
      $customerQuote.selectedCompany = selectedCompany;
      $customerQuote.companyAddress = selectedCompany.address;
      $customerQuote.companyPhone = selectedCompany.phone;

      const idx = companies.findIndex(
        (c) =>
          (c.id != null && c.id === selectedCompany.id) ||
          (c.name && c.name === selectedCompany.name),
      );
      if (idx !== -1) {
        companies[idx] = selectedCompany;
      } else {
        companies = [...companies, selectedCompany];
      }
    }
    showEditCompanyModal = false;
  }

  // Prepared For contact person synchronization & handling
  $: if (
    $customerQuote.contactPerson &&
    $customerQuote.contactPerson !== preparedForInput &&
    !isPreparedForFocused
  ) {
    preparedForInput = $customerQuote.contactPerson;
  }

  $: companyContacts =
    selectedCompany?.contacts && Array.isArray(selectedCompany.contacts)
      ? selectedCompany.contacts
      : selectedCompany?.people && Array.isArray(selectedCompany.people)
        ? selectedCompany.people
        : [];

  $: filteredContacts = companyContacts.filter((c) => {
    if (!preparedForInput.trim()) return true;
    const q = preparedForInput.toLowerCase();
    const nameMatch = c.name && c.name.toLowerCase().includes(q);
    const posMatch = c.position && c.position.toLowerCase().includes(q);
    const emailMatch = c.email && c.email.toLowerCase().includes(q);
    return nameMatch || posMatch || emailMatch;
  });

  $: exactContactMatch = companyContacts.some(
    (c) =>
      c.name &&
      c.name.trim().toLowerCase() === preparedForInput.trim().toLowerCase(),
  );

  function selectContact(contact) {
    if (blurTimeout) {
      clearTimeout(blurTimeout);
      blurTimeout = null;
    }
    preparedForInput = contact.name || "";
    $customerQuote.contactPerson = contact.name || "";
    $customerQuote.prepared_for = contact.name || "";
    $customerQuote.phone = contact.phone || "";
    $customerQuote.email = contact.email || "";
    $customerQuote.position = contact.position || "";
    isPreparedForFocused = false;
  }

  function promptSaveContactDetails(name) {
    if (blurTimeout) {
      clearTimeout(blurTimeout);
      blurTimeout = null;
    }
    isPreparedForFocused = false;
    const targetName = name || preparedForInput.trim();
    if (!targetName) return;
    $customerQuote.contactPerson = targetName;
    $customerQuote.prepared_for = targetName;
    $customerQuote.phone = "";
    $customerQuote.email = "";
    $customerQuote.position = "";
    newContactData = {
      name: targetName,
      position: "",
      phone: "",
      email: "",
    };
    showSaveContactModal = true;
  }

  function handlePreparedForKeyDown(e) {
    if (e.key === "Tab") {
      if (blurTimeout) {
        clearTimeout(blurTimeout);
        blurTimeout = null;
      }
      isPreparedForFocused = false;
      const trimmed = preparedForInput.trim();
      if (!trimmed) {
        $customerQuote.contactPerson = "";
        $customerQuote.prepared_for = "";
        $customerQuote.phone = "";
        $customerQuote.email = "";
        $customerQuote.position = "";
        return;
      }
      const match = companyContacts.find(
        (c) =>
          c.name && c.name.trim().toLowerCase() === trimmed.toLowerCase(),
      );
      if (match) {
        selectContact(match);
      } else {
        $customerQuote.contactPerson = trimmed;
        $customerQuote.prepared_for = trimmed;
        $customerQuote.phone = "";
        $customerQuote.email = "";
        $customerQuote.position = "";
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (blurTimeout) {
        clearTimeout(blurTimeout);
        blurTimeout = null;
      }
      isPreparedForFocused = false;
      const trimmed = preparedForInput.trim();
      if (!trimmed) return;
      const match = companyContacts.find(
        (c) =>
          c.name && c.name.trim().toLowerCase() === trimmed.toLowerCase(),
      );
      if (match) {
        selectContact(match);
      } else {
        $customerQuote.contactPerson = trimmed;
        $customerQuote.prepared_for = trimmed;
        $customerQuote.phone = "";
        $customerQuote.email = "";
        $customerQuote.position = "";
        promptSaveContactDetails(trimmed);
      }
    } else if (e.key === "Escape") {
      isPreparedForFocused = false;
    }
  }

  function handlePreparedForBlur() {
    blurTimeout = setTimeout(() => {
      isPreparedForFocused = false;
      const trimmed = preparedForInput.trim();
      if (!trimmed) {
        $customerQuote.contactPerson = "";
        $customerQuote.prepared_for = "";
        $customerQuote.phone = "";
        $customerQuote.email = "";
        $customerQuote.position = "";
        return;
      }
      const match = companyContacts.find(
        (c) =>
          c.name && c.name.trim().toLowerCase() === trimmed.toLowerCase(),
      );
      if (match) {
        selectContact(match);
      } else {
        $customerQuote.contactPerson = trimmed;
        $customerQuote.prepared_for = trimmed;
        $customerQuote.phone = "";
        $customerQuote.email = "";
        $customerQuote.position = "";
      }
    }, 120);
  }

  async function handleSaveNewContact() {
    if (!newContactData.name || !newContactData.name.trim()) return;

    const contactToSave = {
      name: newContactData.name.trim(),
      position: newContactData.position?.trim() || "",
      phone: newContactData.phone ? toE164(newContactData.phone.trim()) : "",
      email: newContactData.email?.trim() || "",
      main:
        !selectedCompany ||
        !selectedCompany.contacts ||
        selectedCompany.contacts.length === 0,
    };

    if (selectedCompany) {
      if (!Array.isArray(selectedCompany.contacts)) {
        selectedCompany.contacts = [];
      }
      selectedCompany.contacts = [...selectedCompany.contacts, contactToSave];
      selectedCompany.people = selectedCompany.contacts;
      try {
        const saved = await saveCompany(selectedCompany);
        if (saved) {
          selectedCompany = saved;
          if (!selectedCompany.contacts)
            selectedCompany.contacts = selectedCompany.people || [];
          selectedCompany.people = selectedCompany.contacts;
          const idx = companies.findIndex(
            (c) =>
              (c.id != null && c.id === saved.id) ||
              (c.name && c.name === saved.name),
          );
          if (idx !== -1) {
            companies[idx] = selectedCompany;
          } else {
            companies = [...companies, selectedCompany];
          }
        }
      } catch (err) {
        console.error("[handleSaveNewContact] Failed to save contact to DB:", err);
      }
    }

    selectContact(contactToSave);
    showSaveContactModal = false;
    isPreparedForFocused = false;
  }

  function handleUseContactWithoutSaving() {
    const trimmed = (newContactData.name || preparedForInput || "").trim();
    $customerQuote.contactPerson = trimmed;
    $customerQuote.prepared_for = trimmed;
    $customerQuote.phone = newContactData.phone || "";
    $customerQuote.email = newContactData.email || "";
    $customerQuote.position = newContactData.position || "";
    preparedForInput = trimmed;
    showSaveContactModal = false;
    isPreparedForFocused = false;
  }

  // Multi-material packages resolution
  $: packages =
    $customerQuote.haulagePackages && $customerQuote.haulagePackages.length > 0
      ? $customerQuote.haulagePackages
      : $customerQuote.haulagePackage
        ? [$customerQuote.haulagePackage]
        : [];
  $: hasHaulage = packages.length > 0;

  // Active package resolution by unique id
  $: activePkg =
    packages.find((p) => p.id === $customerQuote.activePackageId) ||
    packages.find(
      (p) => (p.material || "Material 1") === $customerQuote.activeMaterialTab,
    ) ||
    packages[0] ||
    null;
  $: pkg = activePkg;

  // Resolve Load & Disposal addresses and Yard information for currently active package in quote
  $: hasPkgYard = Boolean(
    pkg && (
      pkg.yardId != null ||
      pkg.yard != null ||
      pkg.yardName
    )
  );

  $: resolvedPkgYard = (() => {
    if (!hasPkgYard || !pkg) return null;
    const yId = pkg.yardId ?? (typeof pkg.yard === "object" ? pkg.yard?.id : pkg.yard);
    const yName = pkg.yardName ?? (typeof pkg.yard === "object" ? pkg.yard?.name : null);

    const matched = ($yards || []).find((y) =>
      (yId != null && String(y.id) === String(yId)) ||
      (yName && y.name && y.name.trim().toLowerCase() === String(yName).trim().toLowerCase())
    );

    const rawYard = (typeof pkg.yard === "object" && pkg.yard !== null) ? pkg.yard : {};

    return {
      ...(matched || {}),
      ...rawYard,
      contacts: (matched?.contacts && matched.contacts.length > 0) ? matched.contacts : (rawYard.contacts || []),
      image: pkg.image || rawYard.image || matched?.image || null,
      name: pkg.yardName || rawYard.name || matched?.name || "Disposal Yard",
      suburb: rawYard.suburb || matched?.address?.suburb || matched?.suburb || pkg.suburb || ""
    };
  })();

  $: pkgYardMainContact = (() => {
    if (!resolvedPkgYard) return null;
    const contacts = Array.isArray(resolvedPkgYard.contacts) ? resolvedPkgYard.contacts : [];
    const main = contacts.find((c) => c.main === true || c.is_main === true) || contacts[0];
    if (main && (main.name || main.phone || main.email)) {
      return main;
    }
    if (resolvedPkgYard.main_contact && typeof resolvedPkgYard.main_contact === "object") {
      return resolvedPkgYard.main_contact;
    }
    if (resolvedPkgYard.contact && typeof resolvedPkgYard.contact === "object") {
      return resolvedPkgYard.contact;
    }
    if (resolvedPkgYard.contact_name) {
      return {
        name: resolvedPkgYard.contact_name,
        phone: resolvedPkgYard.phone || "",
        email: resolvedPkgYard.email || "",
        position: resolvedPkgYard.position || ""
      };
    }
    return null;
  })();

  $: resolvedPkgLoadAddress = (() => {
    if (!pkg) return null;
    return pkg.load_address || pkg.siteAddress || $customerQuote.siteAddress || $loadAddressText || null;
  })();

  $: resolvedPkgDisposalAddress = (() => {
    if (!pkg) return null;
    return (
      pkg.disposal_address ||
      pkg.dispose_address ||
      pkg.yard?.address ||
      resolvedPkgYard?.address ||
      (pkg.yardName || resolvedPkgYard?.name ? {
        name: pkg.yardName || resolvedPkgYard?.name,
        suburb: pkg.suburb || resolvedPkgYard?.suburb || resolvedPkgYard?.address?.suburb
      } : null) ||
      null
    );
  })();

  let loadAddressWarning = "";
  let disposalAddressWarning = "";

  $: pkgLoadAddressStr = (() => {
    if (!pkg) return "";
    if (typeof pkg.load_address === "string") return pkg.load_address;
    if (pkg.load_address?.place_name) return pkg.load_address.place_name;
    if (pkg.load_address?.street) return inlineAddress(pkg.load_address);
    if (pkg.siteAddress) return pkg.siteAddress;
    if ($customerQuote.siteAddress) return $customerQuote.siteAddress;
    if ($loadAddressText) return $loadAddressText;
    return "";
  })();

  $: pkgDisposalAddressStr = (() => {
    if (!pkg) return "";
    if (typeof pkg.disposal_address === "string") return pkg.disposal_address;
    if (pkg.disposal_address?.place_name) return pkg.disposal_address.place_name;
    if (pkg.disposal_address?.street) return inlineAddress(pkg.disposal_address);
    if (pkg.dispose_address) return typeof pkg.dispose_address === "string" ? pkg.dispose_address : (pkg.dispose_address.place_name || pkg.dispose_address.name || inlineAddress(pkg.dispose_address));
    if (pkg.yard?.address) {
      return typeof pkg.yard.address === "string" ? pkg.yard.address : inlineAddress(pkg.yard.address);
    }
    if (resolvedPkgYard?.address) {
      return typeof resolvedPkgYard.address === "string" ? resolvedPkgYard.address : inlineAddress(resolvedPkgYard.address);
    }
    if (pkg.yardName && pkg.suburb) {
      return `${pkg.yardName}, ${pkg.suburb}`;
    }
    return "";
  })();

  $: pkgYardContactName =
    pkg?.contact_name ||
    pkg?.main_contact?.name ||
    pkgYardMainContact?.name ||
    "";

  $: pkgYardContactPhone =
    pkg?.contact_phone ||
    pkg?.main_contact?.phone ||
    pkgYardMainContact?.phone ||
    "";

  $: pkgYardContactEmail =
    pkg?.contact_email ||
    pkg?.main_contact?.email ||
    pkgYardMainContact?.email ||
    "";

  $: pkgYardContactPosition =
    pkg?.contact_position ||
    pkg?.main_contact?.position ||
    pkgYardMainContact?.position ||
    "";

  function syncSiteDetailsToQuote(targetPkg) {
    if (!targetPkg) return;
    if ($customerQuote.haulagePackage && (
      $customerQuote.haulagePackage.id === targetPkg.id || 
      $customerQuote.haulagePackage.material === targetPkg.material ||
      !$customerQuote.haulagePackages || $customerQuote.haulagePackages.length <= 1
    )) {
      $customerQuote.haulagePackage = {
        ...$customerQuote.haulagePackage,
        load_address: targetPkg.load_address,
        siteAddress: targetPkg.siteAddress,
        load_coordinates: targetPkg.load_coordinates,
        disposal_address: targetPkg.disposal_address,
        dispose_address: targetPkg.dispose_address,
        disposal_coordinates: targetPkg.disposal_coordinates,
        yardId: targetPkg.yardId,
        yardName: targetPkg.yardName,
        yard: targetPkg.yard,
        image: targetPkg.image,
        suburb: targetPkg.suburb,
        contact_name: targetPkg.contact_name,
        contact_phone: targetPkg.contact_phone,
        contact_email: targetPkg.contact_email,
        contact_position: targetPkg.contact_position,
        main_contact: targetPkg.main_contact,
      };
    }
    if (Array.isArray($customerQuote.haulagePackages)) {
      const idx = $customerQuote.haulagePackages.findIndex((p) => 
        (p.id && targetPkg.id && p.id === targetPkg.id) || 
        (p.material && targetPkg.material && p.material.trim().toLowerCase() === targetPkg.material.trim().toLowerCase()) ||
        ($customerQuote.haulagePackages.length === 1)
      );
      if (idx !== -1) {
        $customerQuote.haulagePackages[idx] = {
          ...$customerQuote.haulagePackages[idx],
          load_address: targetPkg.load_address,
          siteAddress: targetPkg.siteAddress,
          load_coordinates: targetPkg.load_coordinates,
          disposal_address: targetPkg.disposal_address,
          dispose_address: targetPkg.dispose_address,
          disposal_coordinates: targetPkg.disposal_coordinates,
          yardId: targetPkg.yardId,
          yardName: targetPkg.yardName,
          yard: targetPkg.yard,
          image: targetPkg.image,
          suburb: targetPkg.suburb,
          contact_name: targetPkg.contact_name,
          contact_phone: targetPkg.contact_phone,
          contact_email: targetPkg.contact_email,
          contact_position: targetPkg.contact_position,
          main_contact: targetPkg.main_contact,
        };
      }
    }
    if (Array.isArray($customerQuote.quote_materials)) {
      const qmIdx = $customerQuote.quote_materials.findIndex(qm => 
        (qm.id && targetPkg.id && String(qm.id) === String(targetPkg.id)) ||
        (qm.material_name && targetPkg.material && qm.material_name.trim().toLowerCase() === targetPkg.material.trim().toLowerCase()) ||
        (qm.name && targetPkg.material && qm.name.trim().toLowerCase() === targetPkg.material.trim().toLowerCase()) ||
        ($customerQuote.quote_materials.length === 1)
      );
      if (qmIdx !== -1) {
        $customerQuote.quote_materials[qmIdx] = {
          ...$customerQuote.quote_materials[qmIdx],
          load_address: targetPkg.load_address,
          siteAddress: targetPkg.siteAddress,
          disposal_address: targetPkg.disposal_address,
          dispose_address: targetPkg.dispose_address,
          yard: targetPkg.yard,
          yard_id: targetPkg.yardId,
          yard_name: targetPkg.yardName,
          image: targetPkg.image,
          suburb: targetPkg.suburb,
          contact_name: targetPkg.contact_name,
          contact_phone: targetPkg.contact_phone,
          contact_email: targetPkg.contact_email,
          contact_position: targetPkg.contact_position,
          main_contact: targetPkg.main_contact,
        };
      }
    }
    packages = [...packages];
    $customerQuote = $customerQuote;
  }

  function handlePkgLoadAddressResult(e) {
    const result = e.detail?.result || e.detail?.detail?.result;
    if (!result || !activePkg) return;
    const placeName = result.place_name || "";
    const isAddressFeature = result.place_type?.includes("address");
    const parsed = parseMapboxAddress(result);
    const validation = validateFullAddress(placeName);

    if (isAddressFeature || validation.valid) {
      loadAddressWarning = "";
    } else {
      loadAddressWarning = "Full address required: MUST include street and suburb (e.g. Church St, Parramatta).";
    }

    const loadAddrObj = {
      street: parsed?.street || validation.street || placeName,
      suburb: parsed?.suburb || validation.suburb || "",
      state: parsed?.state || "NSW",
      postcode: parsed?.postcode || "",
      name: parsed?.name || null,
      place_name: placeName,
      coordinates: parsed?.coordinates || null,
      is_full_address: Boolean(isAddressFeature || validation.valid)
    };

    activePkg.load_address = loadAddrObj;
    activePkg.siteAddress = placeName;
    if (loadAddrObj.coordinates) {
      activePkg.load_coordinates = loadAddrObj.coordinates;
      if (!activePkg.load) activePkg.load = {};
      activePkg.load.coordinates = loadAddrObj.coordinates;
      $loadAddresCoordinate = [loadAddrObj.coordinates.lng, loadAddrObj.coordinates.lat];
    }
    $customerQuote.siteAddress = placeName;
    $loadAddressText = placeName;

    syncSiteDetailsToQuote(activePkg);
  }

  function handlePkgLoadAddressClear() {
    if (!activePkg) return;
    loadAddressWarning = "";
    activePkg.load_address = null;
    activePkg.siteAddress = "";
    activePkg.load_coordinates = null;
    if (activePkg.load) activePkg.load.coordinates = null;
    syncSiteDetailsToQuote(activePkg);
  }

  function handlePkgDisposalAddressResult(e) {
    const result = e.detail?.result || e.detail?.detail?.result;
    if (!result || !activePkg) return;
    const placeName = result.place_name || "";
    const isAddressFeature = result.place_type?.includes("address");
    const parsed = parseMapboxAddress(result);
    const validation = validateFullAddress(placeName);

    if (isAddressFeature || validation.valid) {
      disposalAddressWarning = "";
    } else {
      disposalAddressWarning = "Full address required: MUST include street and suburb.";
    }

    const dispAddrObj = {
      street: parsed?.street || validation.street || placeName,
      suburb: parsed?.suburb || validation.suburb || "",
      state: parsed?.state || "NSW",
      postcode: parsed?.postcode || "",
      name: parsed?.name || null,
      place_name: placeName,
      coordinates: parsed?.coordinates || null,
      is_full_address: Boolean(isAddressFeature || validation.valid)
    };

    activePkg.disposal_address = dispAddrObj;
    activePkg.dispose_address = placeName;
    if (dispAddrObj.suburb) {
      activePkg.suburb = dispAddrObj.suburb;
    }
    if (dispAddrObj.coordinates) {
      activePkg.disposal_coordinates = dispAddrObj.coordinates;
    }

    syncSiteDetailsToQuote(activePkg);
  }

  function handlePkgDisposalAddressClear() {
    if (!activePkg) return;
    disposalAddressWarning = "";
    activePkg.disposal_address = null;
    activePkg.dispose_address = "";
    activePkg.disposal_coordinates = null;
    syncSiteDetailsToQuote(activePkg);
  }

  function handlePkgYardSelectChange(e) {
    const selectedYardId = e.target.value;
    if (!activePkg) return;
    if (!selectedYardId || selectedYardId === "custom") {
      if (selectedYardId === "custom") {
        activePkg.yardId = null;
        activePkg.image = null;
      }
      syncSiteDetailsToQuote(activePkg);
      return;
    }
    const matchedYard = ($yards || []).find((y) => String(y.id) === String(selectedYardId));
    if (matchedYard) {
      activePkg.yardId = matchedYard.id;
      activePkg.yardName = matchedYard.name;
      activePkg.yard = matchedYard;
      activePkg.image = matchedYard.image || null;
      activePkg.suburb = matchedYard.address?.suburb || matchedYard.suburb || "";

      if (matchedYard.address) {
        const addrStr = typeof matchedYard.address === "string" ? matchedYard.address : inlineAddress(matchedYard.address);
        activePkg.disposal_address = {
          street: matchedYard.address.street || matchedYard.name,
          suburb: matchedYard.address.suburb || matchedYard.suburb || "",
          state: matchedYard.address.state || "NSW",
          postcode: matchedYard.address.postcode || "",
          place_name: addrStr,
          coordinates: matchedYard.address.coordinates || null,
          is_full_address: true
        };
        activePkg.dispose_address = addrStr;
      }

      const contacts = Array.isArray(matchedYard.contacts) ? matchedYard.contacts : [];
      const main = contacts.find((c) => c.main === true || c.is_main === true) || contacts[0];
      if (main) {
        activePkg.contact_name = main.name || "";
        activePkg.contact_phone = main.phone || "";
        activePkg.contact_email = main.email || "";
        activePkg.contact_position = main.position || "";
        activePkg.main_contact = { ...main };
      } else if (matchedYard.phone) {
        activePkg.contact_name = matchedYard.contact_name || "Weighbridge / Dispatch";
        activePkg.contact_phone = matchedYard.phone || "";
        activePkg.contact_email = matchedYard.email || "";
        activePkg.contact_position = "Dispatch";
        activePkg.main_contact = {
          name: activePkg.contact_name,
          phone: activePkg.contact_phone,
          email: activePkg.contact_email,
          position: activePkg.contact_position
        };
      }

      syncSiteDetailsToQuote(activePkg);
    }
  }

  function handlePkgYardNameInput(val) {
    if (!activePkg) return;
    activePkg.yardName = val;
    if (typeof activePkg.yard === "object" && activePkg.yard !== null) {
      activePkg.yard.name = val;
    }
    syncSiteDetailsToQuote(activePkg);
  }

  function handlePkgYardSuburbInput(val) {
    if (!activePkg) return;
    activePkg.suburb = val;
    if (typeof activePkg.yard === "object" && activePkg.yard !== null) {
      activePkg.yard.suburb = val;
    }
    syncSiteDetailsToQuote(activePkg);
  }

  function updatePkgContactField(fieldName, value) {
    if (!activePkg) return;
    if (!activePkg.main_contact) activePkg.main_contact = {};
    if (fieldName === "name") {
      activePkg.contact_name = value;
      activePkg.main_contact.name = value;
    } else if (fieldName === "phone") {
      activePkg.contact_phone = value;
      activePkg.main_contact.phone = value;
    } else if (fieldName === "email") {
      activePkg.contact_email = value;
      activePkg.main_contact.email = value;
    } else if (fieldName === "position") {
      activePkg.contact_position = value;
      activePkg.main_contact.position = value;
    }
    syncSiteDetailsToQuote(activePkg);
  }

  function getAddressStreet(addr, fallback = "Address not specified") {
    if (!addr) return fallback;
    if (typeof addr === "string") {
      const val = validateFullAddress(addr);
      if (val.valid && val.street) {
        return val.street;
      }
      const parts = addr.split(",").map((p) => p.trim()).filter(Boolean);
      return parts[0] || addr;
    }
    return addr.street || addr.name || addr.address || fallback;
  }

  function getAddressLocality(addr) {
    if (!addr) return "";
    if (typeof addr === "string") {
      const val = validateFullAddress(addr);
      if (val.valid && val.suburb) {
        const parts = addr.split(",").map((p) => p.trim()).filter(Boolean);
        return parts.slice(1).join(", ") || val.suburb;
      }
      const parts = addr.split(",").map((p) => p.trim()).filter(Boolean);
      return parts.slice(1).join(", ");
    }
    const parts = [];
    if (addr.suburb && addr.suburb !== addr.street && addr.suburb !== addr.name) {
      parts.push(addr.suburb);
    }
    if (addr.state) parts.push(addr.state);
    if (addr.postcode) parts.push(addr.postcode);
    return parts.join(" ");
  }

  $: allocatedTrucks = activePkg
    ? Math.max(1, parseInt(activePkg.schedule?.allocated_trucks ?? activePkg.allocatedTrucks, 10) || 1)
    : 1;

  $: baseTonneRate = hasHaulage && pkg ? Number(pkg.financial_breakdown?.base_disposal_rate ?? pkg.tonneRate ?? 0) : 0;
  $: baseCostPerLoad = hasHaulage && pkg ? Number(pkg.financial_breakdown?.base_cost_per_load ?? pkg.costPerLoad ?? (baseTonneRate * truckCapacity) ?? 0) : 0;
  $: baseDayRate = hasHaulage && pkg ? Number(pkg.fleet?.truck_day_rate ?? pkg.dayRate ?? 1500) : 1500;
  $: truckCapacity =
    hasHaulage && pkg
      ? (
          (Number(pkg.fleet?.truck_capacity_tonne ?? pkg.truckCapacityTonne ?? 19) +
           Number(pkg.fleet?.trailer_capacity_tonne ?? pkg.trailerCapacityTonne ?? 19)) ||
          Number(pkg.fleet?.total_capacity_tonne || pkg.totalCapacity || 38)
        )
      : 38;
  $: tonnes =
    hasHaulage && pkg
      ? Number(
          pkg.totalTonnes != null
            ? pkg.totalTonnes
            : (pkg.tonnesDeliveredInDay || truckCapacity * (pkg.runs || 6) || 0),
        )
      : 0;

  $: totalLoads =
    hasHaulage && pkg
      ? (tonnes > 0 && truckCapacity > 0
          ? Math.ceil(tonnes / truckCapacity)
          : (Number(pkg.schedule?.total_loads ?? pkg.totalLoads) || Number(pkg.runs || 1)))
      : 0;

  $: activePkgFleet =
    hasHaulage && pkg
      ? calculateFleetDaysAndCosts({
          allocatedTrucks,
          totalLoads,
          totalTonnes: tonnes,
          truckCapacity,
          dayRate: baseDayRate,
          startTime: pkg.operating_hours?.start_time || pkg.startTime || "07:00",
          finishTime: pkg.operating_hours?.finish_time || pkg.finishTime || "17:00",
          loadTime: pkg.operating_hours?.load_mins ?? pkg.load_mins ?? (pkg.loadTime != null ? pkg.loadTime : 10),
          tipTime: pkg.operating_hours?.tip_mins ?? pkg.tip_mins ?? (pkg.tipTime != null ? pkg.tipTime : 10),
          routes: pkg.load?.routes || null,
          mapDurationMinutes:
            pkg.travelMins ||
            ($routeTravelEstimates && pkg.yardId
              ? $routeTravelEstimates[pkg.yardId]?.durationMinutes
              : null) ||
            30,
          isEstimate:
            pkg.isEstimate ??
            (!pkg.load || !pkg.load.routes || pkg.load.routes.length === 0),
          singleTruckDays: null,
          dailyRuns1Truck: null,
        })
      : null;

  $: dailyRuns = activePkgFleet?.dailyRuns1Truck || (hasHaulage && pkg ? Number(pkg.schedule?.runs_per_day ?? pkg.runs ?? 0) : 0);

  $: totalDays1Truck = activePkgFleet?.totalDays1Truck || (
    dailyRuns > 0 && totalLoads > 0
      ? totalLoads / dailyRuns
      : (tonnes > 0 && truckCapacity > 0 && dailyRuns > 0
          ? Math.ceil(tonnes / truckCapacity) / dailyRuns
          : Number(pkg?.schedule?.days_single_truck ?? pkg?.totalDays ?? 0))
  );

  $: calculatedDays = activePkgFleet
    ? activePkgFleet.calculatedDays
    : dailyRuns > 0 && totalLoads > 0 && allocatedTrucks > 0
      ? totalLoads / (dailyRuns * allocatedTrucks)
      : totalDays1Truck > 0
        ? totalDays1Truck / allocatedTrucks
        : Number(pkg?.schedule?.estimated_days ?? 0);

  $: daysSplit = getFullAndRemainderDays(calculatedDays);
  $: fullDays = daysSplit.fullDays;
  $: remainderDays = daysSplit.remainderDays;

  function getPackageBaseTolls(p, lds) {
    if (!p) return 0;
    if (p.financial_breakdown?.base_tolls != null) {
      return Number(p.financial_breakdown.base_tolls);
    }
    if (p.schedule?.total_tolls != null) {
      return Number(p.schedule.total_tolls);
    }
    const loadsCount = lds != null ? lds : (p.schedule?.total_loads || p.totalLoads || p.runs || 1);
    if (p.tollPerLoad != null && p.tollPerLoad > 0) {
      return Number((p.tollPerLoad * loadsCount).toFixed(2));
    }
    const load = p.load;
    if (load) {
      const dailyTolls =
        load.total_tolls != null
          ? Number(load.total_tolls)
          : (load.routes || []).reduce((acc, r) => acc + (r.toll || 0), 0) / 100;
      const runs = p.schedule?.runs_per_day || load.total_loads || p.runs || 1;
      const tPerLoad = runs > 0 ? dailyTolls / runs : 0;
      return Number((tPerLoad * loadsCount).toFixed(2));
    }
    if (p.totalTolls != null) {
      const originalLoads = Number(p.schedule?.total_loads || p.totalLoads || p.runs || 1);
      if (originalLoads > 0 && loadsCount !== originalLoads) {
        const tPerLoad = Number(p.totalTolls) / originalLoads;
        return Number((tPerLoad * loadsCount).toFixed(2));
      }
      return Number(p.totalTolls);
    }
    return 0;
  }

  $: baseCartageCost = activePkgFleet
    ? activePkgFleet.baseCartageCost
    : hasHaulage && pkg
      ? allocatedTrucks * fullDays * baseDayRate + baseDayRate * remainderDays
      : 0;

  $: baseTolls = hasHaulage && pkg ? getPackageBaseTolls(pkg, totalLoads) : 0;
  $: baseMaterialCost = hasHaulage && pkg ? tonnes * baseTonneRate : 0;
  $: baseTotalCost =
    hasHaulage && pkg ? baseCartageCost + baseMaterialCost + baseTolls : 0;

  // Fleet Specifications options & handlers
  const truckTypeOptions = [
    { id: "0", text: "6 wheeler - 12t", value: 12 },
    { id: "1", text: "8 wheeler - 15t", value: 15 },
    { id: "2", text: "10 wheeler - 19t", value: 19 },
  ];

  const trailerTypeOptions = [
    { id: "0", text: "None", value: 0 },
    { id: "1", text: "4 wheeler - 12t", value: 12 },
    { id: "2", text: "6 wheeler - 15t", value: 15 },
    { id: "3", text: "8 wheeler - 20t", value: 19 },
  ];

  function getSelectedTruckTypeId(p) {
    if (!p) return "2";
    if (p.truckType != null) {
      const match = truckTypeOptions.find(o => o.id === String(p.truckType) || o.text === p.truckType);
      if (match) return match.id;
    }
    if (p.fleet && p.fleet.truck_type) {
      const match = truckTypeOptions.find(o => o.id === String(p.fleet.truck_type) || o.text === p.fleet.truck_type);
      if (match) return match.id;
    }
    const cap = Number(p.truckCapacityTonne != null ? p.truckCapacityTonne : (p.fleet?.truck_capacity_tonne ?? 19));
    const matchCap = truckTypeOptions.find(o => o.value === cap);
    return matchCap ? matchCap.id : "2";
  }

  function getSelectedTrailerTypeId(p) {
    if (!p) return "3";
    if (p.trailerType != null) {
      const match = trailerTypeOptions.find(o => o.id === String(p.trailerType) || o.text === p.trailerType);
      if (match) return match.id;
    }
    if (p.fleet && p.fleet.trailer_type) {
      const match = trailerTypeOptions.find(o => o.id === String(p.fleet.trailer_type) || o.text === p.fleet.trailer_type);
      if (match) return match.id;
    }
    const cap = Number(p.trailerCapacityTonne != null ? p.trailerCapacityTonne : (p.fleet?.trailer_capacity_tonne ?? 19));
    const matchCap = trailerTypeOptions.find(o => o.value === cap);
    return matchCap ? matchCap.id : "3";
  }

  $: selectedTruckTypeId = getSelectedTruckTypeId(pkg);
  $: selectedTrailerTypeId = getSelectedTrailerTypeId(pkg);

  function handleTruckTypeChange(e) {
    if (!activePkg) return;
    const opt = truckTypeOptions.find(o => o.id === e.target.value);
    if (!opt) return;
    activePkg.truckType = opt.text;
    activePkg.truckCapacityTonne = opt.value;
    updateFleetCalculations(activePkg);
  }

  function handleTrailerTypeChange(e) {
    if (!activePkg) return;
    const opt = trailerTypeOptions.find(o => o.id === e.target.value);
    if (!opt) return;
    activePkg.trailerType = opt.text;
    activePkg.trailerCapacityTonne = opt.value;
    updateFleetCalculations(activePkg);
  }

  function handleTruckCapInput(e) {
    if (!activePkg) return;
    let val = parseFloat(e.target.value);
    if (isNaN(val) || val < 0) val = 0;
    activePkg.truckCapacityTonne = val;
    const match = truckTypeOptions.find(o => o.value === val);
    if (match) {
      activePkg.truckType = match.text;
    }
    updateFleetCalculations(activePkg);
  }

  function handleTrailerCapInput(e) {
    if (!activePkg) return;
    let val = parseFloat(e.target.value);
    if (isNaN(val) || val < 0) val = 0;
    activePkg.trailerCapacityTonne = val;
    const match = trailerTypeOptions.find(o => o.value === val);
    if (match) {
      activePkg.trailerType = match.text;
    }
    updateFleetCalculations(activePkg);
  }

  function handleDayRateInput(e) {
    if (!activePkg) return;
    let val = parseFloat(e.target.value);
    if (isNaN(val) || val < 0) val = 0;
    activePkg.dayRate = val;
    activePkg.truckDayRate = val;
    updateFleetCalculations(activePkg);
  }

  function updateFleetCalculations(targetPkg) {
    if (!targetPkg) return;
    const tCap = Number(targetPkg.truckCapacityTonne != null ? targetPkg.truckCapacityTonne : 19);
    const trCap = Number(targetPkg.trailerCapacityTonne != null ? targetPkg.trailerCapacityTonne : 19);
    const totalCap = tCap + trCap;
    targetPkg.totalCapacity = totalCap;
    const dRate = Number(targetPkg.dayRate != null ? targetPkg.dayRate : 1500);

    if (!targetPkg.fleet) targetPkg.fleet = {};
    targetPkg.fleet.truck_type = targetPkg.truckType || (tCap === 12 ? "6 wheeler - 12t" : (tCap === 15 ? "8 wheeler - 15t" : "10 wheeler - 19t"));
    targetPkg.fleet.truck_capacity_tonne = tCap;
    targetPkg.fleet.trailer_type = targetPkg.trailerType || (trCap === 0 ? "None" : (trCap === 12 ? "4 wheeler - 12t" : (trCap === 15 ? "6 wheeler - 15t" : "8 wheeler - 20t")));
    targetPkg.fleet.trailer_capacity_tonne = trCap;
    targetPkg.fleet.total_capacity_tonne = totalCap;
    targetPkg.fleet.truck_day_rate = dRate;
    targetPkg.fleet.day_rate = dRate;

    const tonnesVal = Number(targetPkg.totalTonnes != null ? targetPkg.totalTonnes : (targetPkg.tonnesDeliveredInDay || 100));
    const newLoads = (tonnesVal > 0 && totalCap > 0) ? Math.ceil(tonnesVal / totalCap) : (targetPkg.runs || 1);
    targetPkg.totalLoads = newLoads;
    const runs = Number(targetPkg.runs || 0);
    targetPkg.totalDays = (runs > 0 && newLoads > 0) ? (newLoads / runs) : 1;

    if ($customerQuote.haulagePackage && ($customerQuote.haulagePackage.id === targetPkg.id || $customerQuote.haulagePackage.material === targetPkg.material)) {
      $customerQuote.haulagePackage.truckType = targetPkg.truckType;
      $customerQuote.haulagePackage.truckCapacityTonne = tCap;
      $customerQuote.haulagePackage.trailerType = targetPkg.trailerType;
      $customerQuote.haulagePackage.trailerCapacityTonne = trCap;
      $customerQuote.haulagePackage.totalCapacity = totalCap;
      $customerQuote.haulagePackage.dayRate = dRate;
      $customerQuote.haulagePackage.truckDayRate = dRate;
      $customerQuote.haulagePackage.fleet = { ...targetPkg.fleet };
      $customerQuote.haulagePackage.totalLoads = newLoads;
      $customerQuote.haulagePackage.totalDays = targetPkg.totalDays;
    }
    if (Array.isArray($customerQuote.haulagePackages)) {
      const match = $customerQuote.haulagePackages.find(p => p.id === targetPkg.id || p.material === targetPkg.material);
      if (match) {
        match.truckType = targetPkg.truckType;
        match.truckCapacityTonne = tCap;
        match.trailerType = targetPkg.trailerType;
        match.trailerCapacityTonne = trCap;
        match.totalCapacity = totalCap;
        match.dayRate = dRate;
        match.truckDayRate = dRate;
        match.fleet = { ...targetPkg.fleet };
        match.totalLoads = newLoads;
        match.totalDays = targetPkg.totalDays;
      }
    }
    packages = [...packages];
    $customerQuote = $customerQuote;
  }

  function handleTonnesInput(e) {
    if (!activePkg) return;
    let val = parseFloat(e.target.value);
    if (isNaN(val) || val < 0) val = 0;
    activePkg.totalTonnes = val;
    const cap = truckCapacity || 38;
    const newLoads = (val > 0 && cap > 0) ? Math.ceil(val / cap) : (activePkg.runs || 1);
    activePkg.totalLoads = newLoads;
    if (activePkg.schedule) {
      activePkg.schedule.total_loads = newLoads;
    }
    const runs = Number(dailyRuns || activePkg.runs || 0);
    activePkg.totalDays = (runs > 0 && newLoads > 0) ? (newLoads / runs) : 1;
    if (activePkg.schedule) {
      activePkg.schedule.days_single_truck = activePkg.totalDays;
    }

    if ($customerQuote.haulagePackage && ($customerQuote.haulagePackage.id === activePkg.id || $customerQuote.haulagePackage.material === activePkg.material)) {
      $customerQuote.haulagePackage.totalTonnes = val;
      $customerQuote.haulagePackage.totalLoads = newLoads;
      $customerQuote.haulagePackage.totalDays = activePkg.totalDays;
      if ($customerQuote.haulagePackage.schedule) {
        $customerQuote.haulagePackage.schedule.total_loads = newLoads;
        $customerQuote.haulagePackage.schedule.days_single_truck = activePkg.totalDays;
      }
    }
    if (Array.isArray($customerQuote.haulagePackages)) {
      const match = $customerQuote.haulagePackages.find(p => p.id === activePkg.id || p.material === activePkg.material);
      if (match) {
        match.totalTonnes = val;
        match.totalLoads = newLoads;
        match.totalDays = activePkg.totalDays;
        if (match.schedule) {
          match.schedule.total_loads = newLoads;
          match.schedule.days_single_truck = activePkg.totalDays;
        }
      }
    }
    packages = [...packages];
    $customerQuote = $customerQuote;
  }

  function handleStartTimeInput(e) {
    if (!activePkg) return;
    const val = e.target.value;
    activePkg.startTime = val;
    if (!activePkg.operating_hours) activePkg.operating_hours = {};
    activePkg.operating_hours.start_time = val;
    syncHoursToQuote(activePkg);
  }

  function handleFinishTimeInput(e) {
    if (!activePkg) return;
    const val = e.target.value;
    activePkg.finishTime = val;
    if (!activePkg.operating_hours) activePkg.operating_hours = {};
    activePkg.operating_hours.finish_time = val;
    syncHoursToQuote(activePkg);
  }

  function handleLoadMinsInput(e) {
    if (!activePkg) return;
    let val = parseInt(e.target.value, 10);
    if (isNaN(val) || val < 0) val = 0;
    activePkg.loadTime = val;
    activePkg.load_mins = val;
    if (!activePkg.operating_hours) activePkg.operating_hours = {};
    activePkg.operating_hours.load_mins = val;
    syncHoursToQuote(activePkg);
  }

  function handleTipMinsInput(e) {
    if (!activePkg) return;
    let val = parseInt(e.target.value, 10);
    if (isNaN(val) || val < 0) val = 0;
    activePkg.tipTime = val;
    activePkg.tip_mins = val;
    if (!activePkg.operating_hours) activePkg.operating_hours = {};
    activePkg.operating_hours.tip_mins = val;
    syncHoursToQuote(activePkg);
  }

  function syncHoursToQuote(targetPkg) {
    if ($customerQuote.haulagePackage && ($customerQuote.haulagePackage.id === targetPkg.id || $customerQuote.haulagePackage.material === targetPkg.material)) {
      $customerQuote.haulagePackage.operating_hours = { ...targetPkg.operating_hours };
      $customerQuote.haulagePackage.startTime = targetPkg.startTime;
      $customerQuote.haulagePackage.finishTime = targetPkg.finishTime;
      $customerQuote.haulagePackage.loadTime = targetPkg.loadTime;
      $customerQuote.haulagePackage.tipTime = targetPkg.tipTime;
    }
    if (Array.isArray($customerQuote.haulagePackages)) {
      const match = $customerQuote.haulagePackages.find(p => p.id === targetPkg.id || p.material === targetPkg.material);
      if (match) {
        match.operating_hours = { ...targetPkg.operating_hours };
        match.startTime = targetPkg.startTime;
        match.finishTime = targetPkg.finishTime;
        match.loadTime = targetPkg.loadTime;
        match.tipTime = targetPkg.tipTime;
      }
    }
    packages = [...packages];
    $customerQuote = $customerQuote;
  }

  function incrementTrucks() {
    if (!activePkg) return;
    const current = Math.max(1, parseInt(activePkg.schedule?.allocated_trucks ?? activePkg.allocatedTrucks, 10) || 1);
    const nextVal = current + 1;
    activePkg.allocatedTrucks = nextVal;
    if (!activePkg.schedule) activePkg.schedule = {};
    activePkg.schedule.allocated_trucks = nextVal;
    $customerQuote.allocatedTrucks = nextVal;
    if ($customerQuote.haulagePackage && ($customerQuote.haulagePackage.id === activePkg.id || $customerQuote.haulagePackage.material === activePkg.material)) {
      $customerQuote.haulagePackage.allocatedTrucks = nextVal;
      if (!$customerQuote.haulagePackage.schedule) $customerQuote.haulagePackage.schedule = {};
      $customerQuote.haulagePackage.schedule.allocated_trucks = nextVal;
    }
    if (Array.isArray($customerQuote.haulagePackages)) {
      const match = $customerQuote.haulagePackages.find(p => p.id === activePkg.id || p.material === activePkg.material);
      if (match) {
        match.allocatedTrucks = nextVal;
        if (!match.schedule) match.schedule = {};
        match.schedule.allocated_trucks = nextVal;
      }
    }
    packages = [...packages];
    $customerQuote = $customerQuote;
  }

  function decrementTrucks() {
    if (!activePkg) return;
    const current = Math.max(1, parseInt(activePkg.schedule?.allocated_trucks ?? activePkg.allocatedTrucks, 10) || 1);
    if (current > 1) {
      const nextVal = current - 1;
      activePkg.allocatedTrucks = nextVal;
      if (!activePkg.schedule) activePkg.schedule = {};
      activePkg.schedule.allocated_trucks = nextVal;
      $customerQuote.allocatedTrucks = nextVal;
      if ($customerQuote.haulagePackage && ($customerQuote.haulagePackage.id === activePkg.id || $customerQuote.haulagePackage.material === activePkg.material)) {
        $customerQuote.haulagePackage.allocatedTrucks = nextVal;
        if (!$customerQuote.haulagePackage.schedule) $customerQuote.haulagePackage.schedule = {};
        $customerQuote.haulagePackage.schedule.allocated_trucks = nextVal;
      }
      if (Array.isArray($customerQuote.haulagePackages)) {
        const match = $customerQuote.haulagePackages.find(p => p.id === activePkg.id || p.material === activePkg.material);
        if (match) {
          match.allocatedTrucks = nextVal;
          if (!match.schedule) match.schedule = {};
          match.schedule.allocated_trucks = nextVal;
        }
      }
      packages = [...packages];
      $customerQuote = $customerQuote;
    }
  }

  function handleTrucksInput(e) {
    if (!activePkg) return;
    let val = parseInt(e.target.value, 10);
    if (isNaN(val) || val < 1) val = 1;
    activePkg.allocatedTrucks = val;
    if (!activePkg.schedule) activePkg.schedule = {};
    activePkg.schedule.allocated_trucks = val;
    $customerQuote.allocatedTrucks = val;
    if ($customerQuote.haulagePackage && ($customerQuote.haulagePackage.id === activePkg.id || $customerQuote.haulagePackage.material === activePkg.material)) {
      $customerQuote.haulagePackage.allocatedTrucks = val;
      if (!$customerQuote.haulagePackage.schedule) $customerQuote.haulagePackage.schedule = {};
      $customerQuote.haulagePackage.schedule.allocated_trucks = val;
    }
    if (Array.isArray($customerQuote.haulagePackages)) {
      const match = $customerQuote.haulagePackages.find(p => p.id === activePkg.id || p.material === activePkg.material);
      if (match) {
        match.allocatedTrucks = val;
        if (!match.schedule) match.schedule = {};
        match.schedule.allocated_trucks = val;
      }
    }
    packages = [...packages];
    $customerQuote = $customerQuote;
  }

  function formatDays(days) {
    if (days === null || days === undefined || isNaN(days) || days <= 0)
      return "0 days";
    const num = Number(days);
    const formatted = num % 1 === 0 ? num.toString() : num.toFixed(1);
    return `${formatted} ${num === 1 ? "day" : "days"}`;
  }

  function applyMargin(base, margin) {
    if (!base || isNaN(base)) return 0;
    if (
      !margin ||
      margin.value === null ||
      margin.value === "" ||
      isNaN(margin.value)
    )
      return base;
    const val = Number(margin.value);
    if (margin.mode === "percent") {
      return base * (1 + val / 100);
    } else {
      return base + val;
    }
  }

  $: currentMargins = activePkg?.margins || $customerQuote.margins;

  function toggleMarginMode(fieldKey) {
    const target = activePkg?.margins || $customerQuote.margins;
    if (!target || !target[fieldKey]) return;
    const currentMode = target[fieldKey].mode;
    target[fieldKey].mode = currentMode === "percent" ? "dollar" : "percent";
    $customerQuote = $customerQuote;
  }

  function resetMargins() {
    const target = activePkg?.margins || $customerQuote.margins;
    if (target) {
      target.tonneRate = { value: 0, mode: "percent" };
      target.costPerLoad = { value: 0, mode: "percent" };
      target.dayRate = { value: 0, mode: "percent" };
      target.cartageCost = { value: 0, mode: "percent" };
      target.totalTolls = { value: 0, mode: "percent" };
      target.totalCost = { value: 0, mode: "percent" };
    }
    $customerQuote = $customerQuote;
  }

  $: mTR = currentMargins?.tonneRate;
  $: mCPL = currentMargins?.costPerLoad;
  $: mDR = currentMargins?.dayRate;
  $: mCC = currentMargins?.cartageCost;
  $: mTL = currentMargins?.totalTolls;
  $: mTC = currentMargins?.totalCost;

  $: hasIndivMargin = !!(
    (mTR && Number(mTR.value) !== 0) ||
    (mCPL && Number(mCPL.value) !== 0) ||
    (mDR && Number(mDR.value) !== 0) ||
    (mCC && Number(mCC.value) !== 0) ||
    (mTL && Number(mTL.value) !== 0)
  );

  $: totalMarginRatio =
    mTC && mTC.mode === "percent"
      ? Number(mTC.value || 0) / 100
      : baseTotalCost > 0
        ? Number(mTC?.value || 0) / baseTotalCost
        : 0;

  $: adjustedTonneRate =
    mTR && Number(mTR.value) !== 0
      ? applyMargin(baseTonneRate, mTR)
      : baseTonneRate * (1 + (hasIndivMargin ? 0 : totalMarginRatio));

  $: adjustedCostPerLoad =
    mCPL && Number(mCPL.value) !== 0
      ? applyMargin(baseCostPerLoad, mCPL)
      : mTR && Number(mTR.value) !== 0
        ? adjustedTonneRate * truckCapacity
        : baseCostPerLoad * (1 + (hasIndivMargin ? 0 : totalMarginRatio));

  $: adjustedDayRate =
    mDR && Number(mDR.value) !== 0
      ? applyMargin(baseDayRate, mDR)
      : baseDayRate * (1 + (hasIndivMargin ? 0 : totalMarginRatio));

  $: adjustedCartageCost =
    mCC && Number(mCC.value) !== 0
      ? applyMargin(baseCartageCost, mCC)
      : mDR && Number(mDR.value) !== 0
        ? allocatedTrucks * fullDays * adjustedDayRate +
          (fullDays === 0 ? allocatedTrucks : 1) *
            adjustedDayRate *
            remainderDays
        : baseCartageCost * (1 + (hasIndivMargin ? 0 : totalMarginRatio));

  $: adjustedTolls =
    mTL && Number(mTL.value) !== 0
      ? applyMargin(baseTolls, mTL)
      : baseTolls * (1 + (hasIndivMargin ? 0 : totalMarginRatio));

  $: intermediateSubtotal = hasIndivMargin
    ? (mTR && Number(mTR.value) !== 0
        ? adjustedTonneRate * tonnes
        : mCPL && Number(mCPL.value) !== 0
          ? adjustedCostPerLoad * totalLoads
          : baseMaterialCost) +
      adjustedCartageCost +
      adjustedTolls
    : baseTotalCost;

  $: subtotal =
    mTC && Number(mTC.value) !== 0 && hasIndivMargin
      ? applyMargin(intermediateSubtotal, mTC)
      : hasIndivMargin
        ? intermediateSubtotal
        : applyMargin(baseTotalCost, mTC);

  $: gst = subtotal * 0.1;
  $: totalIncGst = subtotal + gst;
  $: marginAmount = Math.max(0, subtotal - baseTotalCost);

  // Synchronize domain sub-classes on activePkg
  function syncActivePackageDomainObjects() {
    if (!pkg) return;
    syncSiteDetailsToQuote(pkg);
    if (!pkg.fleet) pkg.fleet = {};
    const tCap = Number(pkg.truckCapacityTonne != null ? pkg.truckCapacityTonne : (pkg.fleet.truck_capacity_tonne ?? 19));
    const trCap = Number(pkg.trailerCapacityTonne != null ? pkg.trailerCapacityTonne : (pkg.fleet.trailer_capacity_tonne ?? 19));
    const totalCap = tCap + trCap;
    pkg.fleet.truck_type = pkg.truckType || pkg.fleet.truck_type || (totalCap <= 24 ? "6 wheeler - 12t" : "10 wheeler - 19t");
    pkg.fleet.truck_capacity_tonne = tCap;
    pkg.fleet.trailer_type = pkg.trailerType || pkg.fleet.trailer_type || "8 wheeler - 20t";
    pkg.fleet.trailer_capacity_tonne = trCap;
    pkg.fleet.total_capacity_tonne = totalCap;
    pkg.fleet.truck_day_rate = baseDayRate;
    pkg.fleet.day_rate = baseDayRate;

    if (!pkg.schedule) pkg.schedule = {};
    pkg.schedule.runs_per_day = dailyRuns;
    pkg.schedule.total_loads = totalLoads;
    pkg.schedule.allocated_trucks = allocatedTrucks;
    pkg.schedule.estimated_days = Number(calculatedDays.toFixed(2));
    pkg.schedule.days_single_truck = Number(totalDays1Truck.toFixed(2));
    pkg.schedule.full_days = fullDays;
    pkg.schedule.remainder_days = remainderDays;
    pkg.schedule.total_tolls = Number(pkg.schedule?.total_tolls ?? pkg.totalTolls ?? baseTolls ?? 0);

    if (!pkg.operating_hours) pkg.operating_hours = {};
    pkg.operating_hours.start_time = pkg.operating_hours.start_time || pkg.startTime || "07:00";
    pkg.operating_hours.finish_time = pkg.operating_hours.finish_time || pkg.finishTime || "17:00";
    pkg.operating_hours.load_mins = Number(pkg.operating_hours.load_mins ?? pkg.load_mins ?? (pkg.loadTime != null ? pkg.loadTime : 15));
    pkg.operating_hours.tip_mins = Number(pkg.operating_hours.tip_mins ?? pkg.tip_mins ?? (pkg.tipTime != null ? pkg.tipTime : 15));

    if (!pkg.financial_breakdown) pkg.financial_breakdown = {};
    pkg.financial_breakdown.base_disposal_rate = baseTonneRate;
    pkg.financial_breakdown.base_cost_per_load = baseCostPerLoad;
    pkg.financial_breakdown.base_day_rate = baseDayRate;
    pkg.financial_breakdown.base_cartage_cost = baseCartageCost;
    pkg.financial_breakdown.base_tolls = baseTolls;
    pkg.financial_breakdown.base_material_cost = baseMaterialCost;
    pkg.financial_breakdown.base_total_cost = baseTotalCost;
    pkg.financial_breakdown.quoted_disposal_rate = adjustedTonneRate;
    pkg.financial_breakdown.quoted_cost_per_load = adjustedCostPerLoad;
    pkg.financial_breakdown.quoted_day_rate = adjustedDayRate;
    pkg.financial_breakdown.quoted_cartage_cost = adjustedCartageCost;
    pkg.financial_breakdown.quoted_tolls = adjustedTolls;
    pkg.financial_breakdown.subtotal_ex_gst = subtotal;
    pkg.financial_breakdown.gst = gst;
    pkg.financial_breakdown.total_inc_gst = totalIncGst;
    pkg.financial_breakdown.effective_rate_per_tonne = tonnes > 0 ? (totalIncGst / tonnes) : 0;
    pkg.financial_breakdown.margin_added = marginAmount;

    if ($customerQuote.haulagePackage && ($customerQuote.haulagePackage.id === pkg.id || $customerQuote.haulagePackage.material === pkg.material || !$customerQuote.haulagePackages || $customerQuote.haulagePackages.length <= 1)) {
      $customerQuote.haulagePackage.fleet = { ...pkg.fleet };
      $customerQuote.haulagePackage.schedule = { ...pkg.schedule };
      $customerQuote.haulagePackage.operating_hours = { ...pkg.operating_hours };
      $customerQuote.haulagePackage.financial_breakdown = { ...pkg.financial_breakdown };
      $customerQuote.haulagePackage.disposal_address = pkg.disposal_address;
      $customerQuote.haulagePackage.dispose_address = pkg.dispose_address;
      $customerQuote.haulagePackage.disposal_coordinates = pkg.disposal_coordinates;
      $customerQuote.haulagePackage.load_address = pkg.load_address;
      $customerQuote.haulagePackage.siteAddress = pkg.siteAddress;
      $customerQuote.haulagePackage.load_coordinates = pkg.load_coordinates;
      $customerQuote.haulagePackage.yard = pkg.yard;
      $customerQuote.haulagePackage.yardId = pkg.yardId;
      $customerQuote.haulagePackage.yardName = pkg.yardName;
      $customerQuote.haulagePackage.suburb = pkg.suburb;
    }
    if (Array.isArray($customerQuote.haulagePackages)) {
      const match = $customerQuote.haulagePackages.find(p => p.id === pkg.id || p.material === pkg.material) || ($customerQuote.haulagePackages.length === 1 ? $customerQuote.haulagePackages[0] : null);
      if (match) {
        match.fleet = { ...pkg.fleet };
        match.schedule = { ...pkg.schedule };
        match.operating_hours = { ...pkg.operating_hours };
        match.financial_breakdown = { ...pkg.financial_breakdown };
        match.disposal_address = pkg.disposal_address;
        match.dispose_address = pkg.dispose_address;
        match.disposal_coordinates = pkg.disposal_coordinates;
        match.load_address = pkg.load_address;
        match.siteAddress = pkg.siteAddress;
        match.load_coordinates = pkg.load_coordinates;
        match.yard = pkg.yard;
        match.yardId = pkg.yardId;
        match.yardName = pkg.yardName;
        match.suburb = pkg.suburb;
      }
    }
  }

  // Financial calculation for ANY package
  function calculatePackageFinancials(p) {
    if (!p) return null;
    const bTR = Number(p.financial_breakdown?.base_disposal_rate ?? p.tonneRate ?? 0);
    const cap = Number(
      p.fleet?.total_capacity_tonne ||
      p.totalCapacity ||
        (Number(p.fleet?.truck_capacity_tonne ?? p.truckCapacityTonne ?? 19) +
          Number(p.fleet?.trailer_capacity_tonne ?? p.trailerCapacityTonne ?? 19)) ||
        38,
    );
    const runs = Number(p.schedule?.runs_per_day ?? p.runs ?? 0);
    const t = Number(
      p.totalTonnes != null
        ? p.totalTonnes
        : (p.tonnesDeliveredInDay || cap * runs || 0),
    );
    const bCPL = Number(p.financial_breakdown?.base_cost_per_load ?? p.costPerLoad ?? (bTR * cap) ?? 0);
    const bDR = Number(p.fleet?.truck_day_rate ?? p.dayRate ?? 1500);
    const lds =
      t > 0 && cap > 0
        ? Math.ceil(t / cap)
        : (p.schedule?.total_loads != null && p.schedule.total_loads > 0
            ? Number(p.schedule.total_loads)
            : (p.totalLoads != null && p.totalLoads > 0 ? Number(p.totalLoads) : runs || 1));
    const allocTrucks = Math.max(1, parseInt(p.schedule?.allocated_trucks ?? p.allocatedTrucks, 10) || 1);
    const singleDays = (runs > 0 && lds > 0) ? (lds / runs) : (Number(p.schedule?.days_single_truck ?? p.totalDays) || 0);
    const pFleet = calculateFleetDaysAndCosts({
      allocatedTrucks: allocTrucks,
      totalLoads: lds,
      totalTonnes: t,
      truckCapacity: cap,
      dayRate: bDR,
      startTime: p.operating_hours?.start_time || p.startTime || "07:00",
      finishTime: p.operating_hours?.finish_time || p.finishTime || "17:00",
      loadTime: p.operating_hours?.load_mins ?? p.load_mins ?? (p.loadTime != null ? p.loadTime : 15),
      tipTime: p.operating_hours?.tip_mins ?? p.tip_mins ?? (p.tipTime != null ? p.tipTime : 15),
      routes: p.load?.routes || null,
      mapDurationMinutes:
        p.travelMins ||
        ($routeTravelEstimates && p.yardId
          ? $routeTravelEstimates[p.yardId]?.durationMinutes
          : null),
      isEstimate:
        p.isEstimate ??
        (!p.load || !p.load.routes || p.load.routes.length === 0),
      singleTruckDays: singleDays,
      dailyRuns1Truck: runs,
    });
    const calcD = pFleet.calculatedDays;
    const bCC = pFleet.baseCartageCost;
    const bMat = t * bTR;
    const bTolls = p.financial_breakdown?.base_tolls != null ? Number(p.financial_breakdown.base_tolls) : getPackageBaseTolls(p, lds);
    const bTotal = bCC + bMat + bTolls;

    const pm = p.margins || {};
    const trM = pm.tonneRate;
    const cplM = pm.costPerLoad;
    const drM = pm.dayRate;
    const ccM = pm.cartageCost;
    const tlM = pm.totalTolls;
    const tcM = pm.totalCost;

    const hasIndiv = !!(
      (trM && Number(trM.value) !== 0) ||
      (cplM && Number(cplM.value) !== 0) ||
      (drM && Number(drM.value) !== 0) ||
      (ccM && Number(ccM.value) !== 0) ||
      (tlM && Number(tlM.value) !== 0)
    );

    const totalRatio =
      tcM && tcM.mode === "percent"
        ? Number(tcM.value || 0) / 100
        : bTotal > 0
          ? Number(tcM?.value || 0) / bTotal
          : 0;

    const adjTR =
      trM && Number(trM.value) !== 0
        ? applyMargin(bTR, trM)
        : bTR * (1 + (hasIndiv ? 0 : totalRatio));

    const adjCPL =
      cplM && Number(cplM.value) !== 0
        ? applyMargin(bCPL, cplM)
        : trM && Number(trM.value) !== 0
          ? adjTR * cap
          : bCPL * (1 + (hasIndiv ? 0 : totalRatio));

    const adjDR =
      drM && Number(drM.value) !== 0
        ? applyMargin(bDR, drM)
        : bDR * (1 + (hasIndiv ? 0 : totalRatio));

    const split = getFullAndRemainderDays(calcD);
    const adjCC =
      ccM && Number(ccM.value) !== 0
        ? applyMargin(bCC, ccM)
        : drM && Number(drM.value) !== 0
          ? allocTrucks * split.fullDays * adjDR +
            (split.fullDays === 0 ? allocTrucks : 1) *
              adjDR *
              split.remainderDays
          : bCC * (1 + (hasIndiv ? 0 : totalRatio));

    const adjTolls =
      tlM && Number(tlM.value) !== 0
        ? applyMargin(bTolls, tlM)
        : bTolls * (1 + (hasIndiv ? 0 : totalRatio));

    const interSub = hasIndiv
      ? (trM && Number(trM.value) !== 0
          ? adjTR * t
          : cplM && Number(cplM.value) !== 0
            ? adjCPL * lds
            : bMat) +
        adjCC +
        adjTolls
      : bTotal;

    const pkgSubtotal =
      tcM && Number(tcM.value) !== 0 && hasIndiv
        ? applyMargin(interSub, tcM)
        : hasIndiv
          ? interSub
          : applyMargin(bTotal, tcM);

    const pkgGst = pkgSubtotal * 0.1;
    const pkgTotalIncGst = pkgSubtotal + pkgGst;
    return {
      tonnes: t,
      loads: lds,
      baseTotal: bTotal,
      cartage: adjCC,
      disposal:
        trM && Number(trM.value) !== 0
          ? adjTR * t
          : cplM && Number(cplM.value) !== 0
            ? adjCPL * lds
            : bMat,
      tolls: adjTolls,
      subtotalExGst: pkgSubtotal,
      gst: pkgGst,
      totalIncGst: pkgTotalIncGst,
      ratePerTonne: t > 0 ? pkgTotalIncGst / t : 0,
    };
  }

  // Combined totals across all packages
  $: combinedFinancials = packages.reduce(
    (acc, p) => {
      const fin = calculatePackageFinancials(p);
      if (fin) {
        acc.totalTonnes += fin.tonnes;
        acc.totalLoads += fin.loads;
        acc.subtotalExGst += fin.subtotalExGst;
        acc.gst += fin.gst;
        acc.totalIncGst += fin.totalIncGst;
        acc.cartage += fin.cartage;
        acc.disposal += fin.disposal;
        acc.tolls += fin.tolls;
        acc.baseTotal += fin.baseTotal;
      }
      return acc;
    },
    {
      totalTonnes: 0,
      totalLoads: 0,
      subtotalExGst: 0,
      gst: 0,
      totalIncGst: 0,
      cartage: 0,
      disposal: 0,
      tolls: 0,
      baseTotal: 0,
    },
  );

  $: combinedMarginAmount = Math.max(
    0,
    combinedFinancials.subtotalExGst - combinedFinancials.baseTotal,
  );

  function switchTab(p) {
    if (!p) return;
    syncActivePackageDomainObjects();
    $customerQuote.activePackageId = p.id;
    $customerQuote.activeMaterialTab = p.material || "Material 1";
    $customerQuote.haulagePackage = p;
    $customerQuote = $customerQuote;
  }

  function addMaterialPackage() {
    const newIdx = packages.length + 1;
    const newPkgId = `pkg_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    const newMatName = `Material ${newIdx}`;
    const newPackage = {
      id: newPkgId,
      material: newMatName,
      siteAddress: $customerQuote.siteAddress || $loadAddressText || "",
      load_address: { street: $customerQuote.siteAddress || $loadAddressText || "" },
      yardName: "Genesis Recycling & Waste",
      suburb: "Eastern Creek",
      tonneRate: 38,
      costPerLoad: 1140,
      dayRate: 1500,
      truckCapacityTonne: 19,
      trailerCapacityTonne: 19,
      totalCapacity: 38,
      runs: 5,
      totalTonnes: 500,
      allocatedTrucks: 1,
      margins: {
        tonneRate: { value: 0, mode: "percent" },
        costPerLoad: { value: 0, mode: "percent" },
        dayRate: { value: 0, mode: "percent" },
        cartageCost: { value: 0, mode: "percent" },
        totalTolls: { value: 0, mode: "percent" },
        totalCost: { value: $customerQuote.marginPercent || 15, mode: "percent" },
      },
      fleet: {
        truck_type: "10 wheeler - 19t",
        truck_capacity_tonne: 19,
        trailer_type: "8 wheeler - 20t",
        trailer_capacity_tonne: 19,
        total_capacity_tonne: 38,
        truck_day_rate: 1500,
        day_rate: 1500,
      },
      schedule: {
        runs_per_day: 5,
        total_loads: 14,
        allocated_trucks: 1,
        estimated_days: 2.8,
        days_single_truck: 2.8,
        full_days: 2,
        remainder_days: 0.8,
        total_tolls: 0,
      },
      operating_hours: {
        start_time: "07:00",
        finish_time: "17:00",
        load_mins: 15,
        tip_mins: 15,
      },
      financial_breakdown: {
        base_disposal_rate: 38,
        base_cost_per_load: 1444,
        base_day_rate: 1500,
        base_cartage_cost: 4200,
        base_tolls: 0,
        base_material_cost: 19000,
        base_total_cost: 23200,
        quoted_disposal_rate: 38,
        quoted_cost_per_load: 1444,
        quoted_day_rate: 1500,
        quoted_cartage_cost: 4200,
        quoted_tolls: 0,
        subtotal_ex_gst: 26680,
        gst: 2668,
        total_inc_gst: 29348,
        effective_rate_per_tonne: 58.70,
        margin_added: 3480,
      },
    };

    if (!$customerQuote.haulagePackages) {
      $customerQuote.haulagePackages = [newPackage];
    } else {
      $customerQuote.haulagePackages = [...$customerQuote.haulagePackages, newPackage];
    }
    switchTab(newPackage);
  }

  function removePackage(pkgId) {
    if (!$customerQuote.haulagePackages) return;
    const removeIdx = $customerQuote.haulagePackages.findIndex(
      (p) => p.id === pkgId,
    );
    if (removeIdx !== -1) {
      $customerQuote.haulagePackages.splice(removeIdx, 1);
      $customerQuote.haulagePackages = [...$customerQuote.haulagePackages];
      if ($customerQuote.haulagePackages.length > 0) {
        switchTab($customerQuote.haulagePackages[0]);
      } else {
        $customerQuote.activePackageId = null;
        $customerQuote.activeMaterialTab = "";
        $customerQuote.haulagePackage = null;
      }
      $customerQuote = $customerQuote;
    }
  }

  function handleNewQuote() {
    $customerQuote = {
      id: null,
      quoteNumber: null,
      quoteDate: new Date().toISOString().split("T")[0],
      validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      status: "draft",
      customerName: "",
      contactPerson: "",
      prepared_for: "",
      phone: "",
      email: "",
      selectedCompany: null,
      companyAddress: null,
      companyPhone: "",
      notes:
        "Rates based on standard operating hours (07:00 - 17:00). Tolls and disposal fees included. Subject to clean tipping guidelines.",
      paymentTerms: "Strictly 14 Days from date of invoice",
      payment_terms: "Strictly 14 Days from date of invoice",
      marginPercent: 15,
      margins: {
        tonneRate: { value: 0, mode: "percent" },
        costPerLoad: { value: 0, mode: "percent" },
        dayRate: { value: 0, mode: "percent" },
        cartageCost: { value: 0, mode: "percent" },
        totalTolls: { value: 0, mode: "percent" },
        totalCost: { value: 15, mode: "percent" },
      },
      allocatedTrucks: 1,
      haulagePackage: null,
      haulagePackages: [],
      activeMaterialTab: "",
    };
    selectedCompany = null;
    preparedForInput = "";
    companySearchTerm = "";
    saveFeedbackMessage = "";
  }

  async function handleSaveQuote() {
    if (isSavingQuote) return;
    syncActivePackageDomainObjects();

    if (!isCompanySelected) {
      saveFeedbackType = "error";
      saveFeedbackMessage = "Company is a required field. Please select or add a company.";
      return;
    }

    if (!$customerQuote.customerName && selectedCompany?.name) {
      $customerQuote.customerName = selectedCompany.name;
    }
    if (preparedForInput && !$customerQuote.contactPerson) {
      $customerQuote.contactPerson = preparedForInput.trim();
    }
    $customerQuote.prepared_for =
      $customerQuote.contactPerson || preparedForInput || "";

    if ($customerQuote.phone && $customerQuote.phone.trim()) {
      $customerQuote.phone = toE164($customerQuote.phone.trim());
    }
    if (selectedCompany) {
      $customerQuote.selectedCompany = selectedCompany;
      $customerQuote.companyAddress = selectedCompany.address || null;
      $customerQuote.companyPhone = selectedCompany.phone || "";
    }

    $customerQuote.payment_terms = $customerQuote.paymentTerms || $customerQuote.payment_terms || "Strictly 14 Days from date of invoice";
    $customerQuote.paymentTerms = $customerQuote.payment_terms;

    isSavingQuote = true;
    saveFeedbackMessage = "";
    saveFeedbackType = "";
    try {
      const saved = await saveQuote($customerQuote);
      if (saved && saved.id) {
        $customerQuote.id = saved.id;
      }
      if (saved && (saved.quote_number || saved.quoteNumber)) {
        $customerQuote.quoteNumber = saved.quote_number || saved.quoteNumber;
      }
      if (saved && Array.isArray(saved.quote_materials)) {
        saved.quote_materials.forEach((m, idx) => {
          if ($customerQuote.haulagePackages && $customerQuote.haulagePackages[idx]) {
            const hp = $customerQuote.haulagePackages[idx];
            if (m.disposal_address || m.dispose_address) {
              hp.disposal_address = m.disposal_address || m.dispose_address;
              hp.dispose_address = typeof m.dispose_address === "string" ? m.dispose_address : (m.dispose_address?.place_name || m.dispose_address?.name || inlineAddress(m.dispose_address));
            }
            if (m.load_address) {
              hp.load_address = m.load_address;
              hp.siteAddress = typeof m.load_address === "string" ? m.load_address : (m.load_address?.place_name || m.load_address?.name || inlineAddress(m.load_address));
            }
            if (m.yard) {
              hp.yard = m.yard;
              hp.yardId = m.yard.id ?? hp.yardId;
              hp.yardName = m.yard.name ?? hp.yardName;
            }
          }
        });
        if (activePkg) {
          const matchSaved = saved.quote_materials.find(m => 
            (m.material_name && activePkg.material && m.material_name.trim().toLowerCase() === activePkg.material.trim().toLowerCase())
          ) || saved.quote_materials[0];
          if (matchSaved) {
            if (matchSaved.disposal_address || matchSaved.dispose_address) {
              activePkg.disposal_address = matchSaved.disposal_address || matchSaved.dispose_address;
              activePkg.dispose_address = typeof matchSaved.dispose_address === "string" ? matchSaved.dispose_address : (matchSaved.dispose_address?.place_name || matchSaved.dispose_address?.name || inlineAddress(matchSaved.dispose_address));
            }
            if (matchSaved.load_address) {
              activePkg.load_address = matchSaved.load_address;
              activePkg.siteAddress = typeof matchSaved.load_address === "string" ? matchSaved.load_address : (matchSaved.load_address?.place_name || matchSaved.load_address?.name || inlineAddress(matchSaved.load_address));
            }
          }
        }
        $customerQuote.haulagePackages = [...($customerQuote.haulagePackages || [])];
        $customerQuote.haulagePackage = activePkg ? { ...activePkg } : $customerQuote.haulagePackage;
        $customerQuote = $customerQuote;
      }
      saveFeedbackType = "success";
      saveFeedbackMessage = `Quote successfully saved! (ID: #${$customerQuote.id || saved.id})`;
      await loadQuotes();
      return saved;
    } catch (err) {
      console.error("[handleSaveQuote] Error saving quote:", err);
      saveFeedbackType = "error";
      saveFeedbackMessage = `Failed to save quote: ${err.message || err}`;
      return null;
    } finally {
      isSavingQuote = false;
      if (saveFeedbackTimeout) clearTimeout(saveFeedbackTimeout);
      saveFeedbackTimeout = setTimeout(() => {
        saveFeedbackMessage = "";
        saveFeedbackType = "";
      }, 5000);
    }
  }

  async function handleStatusChange(e) {
    const newStatus = e.target.value;
    $customerQuote.status = newStatus;

    if ($customerQuote.id) {
      try {
        await updateQuoteDoc($customerQuote.id, { status: newStatus });
        const qIdx = quotes.findIndex(
          (q) => String(q.id || q._id) === String($customerQuote.id)
        );
        if (qIdx !== -1) {
          quotes[qIdx] = { ...quotes[qIdx], status: newStatus };
          quotes = [...quotes];
        }
        saveFeedbackType = "success";
        saveFeedbackMessage = `Quote status updated to "${newStatus}".`;
        if (saveFeedbackTimeout) clearTimeout(saveFeedbackTimeout);
        saveFeedbackTimeout = setTimeout(() => {
          saveFeedbackMessage = "";
          saveFeedbackType = "";
        }, 3000);
      } catch (err) {
        console.error("[handleStatusChange] Error:", err);
        saveFeedbackType = "error";
        saveFeedbackMessage = `Failed to update status: ${err.message || err}`;
      }
    }
  }

  async function handleCreateJobFromQuote() {
    if (isCreatingJob) return;
    isCreatingJob = true;
    saveFeedbackMessage = "";
    saveFeedbackType = "";
    try {
      syncActivePackageDomainObjects();

      let qId = $customerQuote.id;
      if (!qId) {
        saveFeedbackMessage = "Saving quote before creating job...";
        saveFeedbackType = "success";
        const saved = await handleSaveQuote();
        if (!saved && !$customerQuote.id) {
          saveFeedbackMessage = "Please ensure customer details are valid.";
          saveFeedbackType = "error";
          isCreatingJob = false;
          return;
        }
        qId = $customerQuote.id || saved?.id;
      } else {
        await handleSaveQuote();
        qId = $customerQuote.id;
      }

      saveFeedbackMessage = "Generating and saving job to database...";
      saveFeedbackType = "success";

      const dispatchParams = {
        po_number: $customerQuote.po_number || $customerQuote.poNumber || "",
        start_date: $customerQuote.start_date || $customerQuote.quoteDate || new Date().toISOString().split("T")[0],
        site_notes: $customerQuote.notes || "",
        notes: $customerQuote.notes || ""
      };

      const result = await generateJobFromQuote(qId, "proposed", dispatchParams);
      const createdJob = result?.job || (result?.id ? result : null);

      $customerQuote.status = "converted";
      try {
        await updateQuoteDoc(qId, { status: "converted" });
      } catch (e) {
        console.warn("[handleCreateJobFromQuote] Could not persist converted status:", e);
      }

      const qIdx = quotes.findIndex(
        (q) => String(q.id || q._id) === String(qId)
      );
      if (qIdx !== -1) {
        quotes[qIdx] = { ...quotes[qIdx], status: "converted" };
        quotes = [...quotes];
      }

      $selectedQuoteForJob = { ...$customerQuote, status: "converted" };
      if (createdJob) {
        $selectedJobForEdit = createdJob;
      }

      $activeTab = "create-job";
    } catch (err) {
      console.error("[handleCreateJobFromQuote] Error:", err);
      saveFeedbackMessage = `Failed to create job: ${err.message || err}`;
      saveFeedbackType = "error";
    } finally {
      isCreatingJob = false;
      if (saveFeedbackTimeout) clearTimeout(saveFeedbackTimeout);
      saveFeedbackTimeout = setTimeout(() => {
        saveFeedbackMessage = "";
        saveFeedbackType = "";
      }, 5000);
    }
  }

  async function printQuote() {
    try {
      pdfFeedback = true;
      await generateQuotePdf($customerQuote);
      if (pdfFeedbackTimeout) clearTimeout(pdfFeedbackTimeout);
      pdfFeedbackTimeout = setTimeout(() => (pdfFeedback = false), 2500);
    } catch (err) {
      console.error("PDF generation failed:", err);
      alert("Failed to generate PDF quote: " + err.message);
      pdfFeedback = false;
    }
  }

  async function copyQuoteSummary() {
    let text = `==============================\n`;
    text += `TRUCKTRACK FORMAL CUSTOMER QUOTE\n`;
    text += `Quote #: ${$customerQuote.quoteNumber || ($customerQuote.id ? `QT-${$customerQuote.id}` : "Draft")}\n`;
    text += `Date: ${$customerQuote.quoteDate} (Valid until ${$customerQuote.validUntil})\n`;
    text += `Client: ${$customerQuote.customerName || "N/A"}\n`;
    text += `Prepared For: ${$customerQuote.contactPerson || $customerQuote.prepared_for || "—"}\n`;
    text += `Phone: ${$customerQuote.phone || "—"} | Email: ${$customerQuote.email || "—"}\n\n`;

    if (packages.length > 0) {
      packages.forEach((p, idx) => {
        const fin = calculatePackageFinancials(p);
        text += `[Material Package ${idx + 1}: ${p.material || "Material"}]\n`;
        text += `  Disposal Yard: ${p.yardName || "Tipping Yard"}\n`;
        text += `  Site: ${p.siteAddress || "Site Location"}\n`;
        text += `  Volume: ${fin?.tonnes || 0} tonnes (${fin?.loads || 0} loads)\n`;
        text += `  Subtotal: ${formatCurrency(fin?.subtotalExGst || 0)} (inc GST: ${formatCurrency(fin?.totalIncGst || 0)})\n\n`;
      });
      text += `==============================\n`;
      text += `GRAND TOTAL (inc GST): ${formatCurrency(combinedFinancials.totalIncGst)}\n`;
      text += `==============================\n`;
    }

    try {
      await navigator.clipboard.writeText(text);
      copyFeedback = true;
      if (copyFeedbackTimeout) clearTimeout(copyFeedbackTimeout);
      copyFeedbackTimeout = setTimeout(() => (copyFeedback = false), 2500);
    } catch (_) {
      alert("Quote copied to clipboard!");
    }
  }

  function toggleSidebar() {
    isSidebarOpen = !isSidebarOpen;
  }

  function handleSelectQuote(q) {
    if (!q) return;
    fetchAndLoadQuote(q.id || q._id, q);
  }

  async function handleOpenInCalculate() {
    try {
      // If the quote has customer details and an assigned ID, persist latest edits
      if ($customerQuote.customerName?.trim() && $customerQuote.id) {
        try {
          await handleSaveQuote();
        } catch (e) {
          console.warn("[handleOpenInCalculate] Auto-save before navigation warning:", e);
        }
      }

      // Ensure Calculate customer quote window is set to open and visible
      $showCustomerQuoteWindow = true;

      // Sync site address, coordinates, and material to Calculate stores if available
      const firstPkg = ($customerQuote.haulagePackages || [])[0] || $customerQuote.haulagePackage;
      const siteAddr = firstPkg?.siteAddress || 
        (typeof firstPkg?.load_address === "string" ? firstPkg.load_address : firstPkg?.load_address?.street) ||
        ($customerQuote.selectedCompany?.address?.street ? `${$customerQuote.selectedCompany.address.street}, ${$customerQuote.selectedCompany.address.suburb || ""}` : "");
      
      if (siteAddr) {
        $loadAddressText = siteAddr;
      }

      const coords = firstPkg?.coordinates || firstPkg?.load?.coordinates;
      if (coords && Array.isArray(coords) && coords.length >= 2) {
        $loadAddresCoordinate = coords;
      }

      if (firstPkg?.material) {
        $quoteSelectedMaterial = firstPkg.material;
      }

      // Navigate to Calculate tab
      $activeTab = "quotes";
    } catch (err) {
      console.error("[handleOpenInCalculate] Error navigating to Calculate:", err);
      $activeTab = "quotes";
    }
  }

  function formatCurrency(val) {
    if (val === null || val === undefined || isNaN(val)) return "$0.00";
    return (
      "$" +
      Number(val).toLocaleString("en-AU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }

  function formatTonnes(val) {
    if (val === null || val === undefined || isNaN(val)) return "0 t";
    return Number(val).toLocaleString("en-AU") + " t";
  }

  function getQuoteNormalizedStatus(q) {
    if (!q) return "draft";
    const st = (q.status || "").toLowerCase().trim();
    if (st === "converted" || !!q.job_id || !!q.converted_to_job) return "converted";
    if (st === "accepted") return "accepted";
    if (st === "sent") return "sent";
    if (st === "declined") return "declined";
    return "draft";
  }

  $: statusCounts = quotes.reduce((acc, q) => {
    const st = getQuoteNormalizedStatus(q);
    acc[st] = (acc[st] || 0) + 1;
    return acc;
  }, {});

  $: filteredQuotes = quotes.filter((q) => {
    // 1. Status Filter
    if (selectedStatuses.length === 0) return false;
    const st = getQuoteNormalizedStatus(q);
    if (!selectedStatuses.includes(st)) return false;

    // 2. Search query filter
    if (!searchQuery.trim()) return true;
    const term = searchQuery.toLowerCase().trim();
    const qNum = String(q.quote_number || q.id || "").toLowerCase();
    const custName = String(
      q.customer_summary?.name || (typeof q.customer === "object" ? q.customer?.name : q.customer) || q.customerName || "",
    ).toLowerCase();
    const site = String(
      (typeof q.customer_summary?.address === "string" ? q.customer_summary.address : q.customer_summary?.address?.street) ||
      (typeof q.customer?.site_address === "string" ? q.customer.site_address : q.customer?.site_address?.street) ||
      q.siteAddress || "",
    ).toLowerCase();
    const mats = Array.isArray(q.quote_materials)
      ? q.quote_materials
          .map((m) => (m.material_name || m.name || "").toLowerCase())
          .join(" ")
      : "";

    return (
      qNum.includes(term) ||
      custName.includes(term) ||
      site.includes(term) ||
      mats.includes(term)
    );
  });

  function getQuoteCustomerName(q) {
    return (
      q.customer_summary?.name ||
      (typeof q.customer === "object" ? q.customer?.name : q.customer) ||
      q.customerName ||
      "Unnamed Client"
    );
  }

  function getQuoteSiteAddress(q) {
    if (q.customer?.site_address) {
      if (typeof q.customer.site_address === "string")
        return q.customer.site_address;
      const a = q.customer.site_address;
      return [a.street, a.suburb].filter(Boolean).join(", ");
    }
    if (q.quote_materials && q.quote_materials[0]?.load_address) {
      const a = q.quote_materials[0].load_address;
      if (typeof a === "string") return a;
      return [a.street, a.suburb].filter(Boolean).join(", ");
    }
    return "Site address not specified";
  }

  function getQuoteTotalTonnes(q) {
    if (q && $customerQuote && (String(q.id || q._id) === String($customerQuote.id || $customerQuote._id) || (q.quote_number && q.quote_number === $customerQuote.quoteNumber))) {
      return combinedFinancials.totalTonnes;
    }
    if (!Array.isArray(q.quote_materials)) return 0;
    return q.quote_materials.reduce(
      (sum, m) =>
        sum + (Number(m.total_tonnes || m.quantity || m.amount) || 0),
      0,
    );
  }

  function getQuoteGrandTotal(q) {
    if (q && $customerQuote && (String(q.id || q._id) === String($customerQuote.id || $customerQuote._id) || (q.quote_number && q.quote_number === $customerQuote.quoteNumber))) {
      return combinedFinancials.totalIncGst || totalIncGst;
    }
    if (
      q.financial_summary &&
      q.financial_summary.grand_total_inc_gst != null
    ) {
      return Number(q.financial_summary.grand_total_inc_gst);
    }
    if (!Array.isArray(q.quote_materials)) return 0;
    return q.quote_materials.reduce((sum, m) => {
      const p =
        Number(
          m.proposed_total_price ||
            m.total_price ||
            m.financial_breakdown?.total_inc_gst,
        ) || 0;
      return sum + p;
    }, 0);
  }
</script>

<svelte:window on:click={handleClickOutside} on:keydown={handleWindowKeydown} />

<div class="studio-layout">
  <!-- Left Column: Quotes Navigator Sidebar (Collapsible) -->
  <aside class="quotes-sidebar" class:collapsed={!isSidebarOpen}>
    <div class="sidebar-header">
      <div class="sidebar-actions-row">
        <div class="status-multiselect-wrap" bind:this={multiselectContainer}>
          <!-- Native multi-select for accessibility and testability -->
          <select
            multiple
            id="quote-status-filter-select"
            class="sr-only"
            bind:value={selectedStatuses}
            aria-label="Filter quotes by status"
          >
            {#each STATUS_OPTIONS as opt}
              <option value={opt.value}>{opt.label}</option>
            {/each}
          </select>

          <!-- Custom Multi-Select Trigger Button -->
          <button
            type="button"
            class="status-multiselect-trigger {isStatusDropdownOpen ? 'open' : ''}"
            on:click={() => (isStatusDropdownOpen = !isStatusDropdownOpen)}
            aria-haspopup="listbox"
            aria-expanded={isStatusDropdownOpen}
            title="Click to filter quotes by status"
          >
            <div class="trigger-label-group">
              <Filter size={13} class="filter-icon" />
              <span class="trigger-title">Status:</span>
              <div class="trigger-chips-container">
                {#if selectedStatuses.length === 0}
                  <span class="placeholder-text">None selected</span>
                {:else if selectedStatuses.length === STATUS_OPTIONS.length}
                  <span class="status-chip chip-all">All ({filteredQuotes.length})</span>
                {:else if selectedStatuses.length <= 3}
                  {#each selectedStatuses as st}
                    {@const opt = STATUS_OPTIONS.find((o) => o.value === st)}
                    {#if opt}
                      <span
                        class="status-chip"
                        style="background: {opt.bg}; color: {opt.color}; border: 1px solid {opt.border};"
                      >
                        <span class="chip-dot" style="background: {opt.color};"></span>
                        <span class="chip-text">{opt.label}</span>
                        <button
                          type="button"
                          class="chip-remove"
                          on:click|stopPropagation={() => toggleStatusFilter(opt.value)}
                          title="Remove {opt.label}"
                        >
                          &times;
                        </button>
                      </span>
                    {/if}
                  {/each}
                {:else}
                  {@const firstOpt = STATUS_OPTIONS.find((o) => o.value === selectedStatuses[0])}
                  {#if firstOpt}
                    <span
                      class="status-chip"
                      style="background: {firstOpt.bg}; color: {firstOpt.color}; border: 1px solid {firstOpt.border};"
                    >
                      <span class="chip-dot" style="background: {firstOpt.color};"></span>
                      <span class="chip-text">{firstOpt.label}</span>
                    </span>
                  {/if}
                  <span
                    class="status-chip chip-more"
                    title={selectedStatuses.map((s) => s[0].toUpperCase() + s.slice(1)).join(', ')}
                  >
                    +{selectedStatuses.length - 1} more
                  </span>
                {/if}
              </div>
            </div>
            <div class="trigger-arrow">
              <ChevronDown size={14} class="chevron {isStatusDropdownOpen ? 'rotated' : ''}" />
            </div>
          </button>

          <!-- Multi-Select Dropdown Menu -->
          {#if isStatusDropdownOpen}
            <div class="status-dropdown-menu" role="listbox">
              <div class="dropdown-header">
                <span class="dropdown-heading">Filter by Status</span>
                <div class="dropdown-quick-links">
                  <button
                    type="button"
                    class="quick-link"
                    on:click={resetStatusToDefault}
                    title="Reset to Draft, Sent, Accepted"
                  >
                    Default
                  </button>
                  <span class="quick-bullet">&bull;</span>
                  <button type="button" class="quick-link" on:click={selectAllStatuses}>
                    All
                  </button>
                  <span class="quick-bullet">&bull;</span>
                  <button type="button" class="quick-link" on:click={clearAllStatuses}>
                    Clear
                  </button>
                </div>
              </div>
              <div class="dropdown-items-list">
                {#each STATUS_OPTIONS as opt}
                  {@const isChecked = selectedStatuses.includes(opt.value)}
                  {@const count = statusCounts[opt.value] || 0}
                  <label class="dropdown-item-row {isChecked ? 'checked' : ''}">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      on:change={() => toggleStatusFilter(opt.value)}
                      class="status-checkbox"
                    />
                    <span class="status-indicator-dot" style="background: {opt.color};"></span>
                    <span class="status-name">{opt.label}</span>
                    <span class="status-item-count" class:has-items={count > 0}>({count})</span>
                  </label>
                {/each}
              </div>
            </div>
          {/if}
        </div>
        <button
          class="sidebar-close-btn"
          on:click={() => (isSidebarOpen = false)}
          title="Slide sidebar out"
          type="button"
          aria-label="Collapse Sidebar"
        >
          <ChevronLeft size={18} />
        </button>
      </div>

      <!-- Search Input -->
      <div class="search-box">
        <Search size={16} class="search-icon" />
        <input
          type="text"
          placeholder="Search quotes by #, client, site, material..."
          bind:value={searchQuery}
          class="search-input"
        />
        {#if searchQuery}
          <button class="clear-btn" on:click={() => (searchQuery = "")} type="button">&times;</button>
        {/if}
      </div>

      <!-- Create a New Quote Button -->
      <button
        class="create-new-quote-btn"
        on:click={handleNewQuote}
        type="button"
        title="Start a fresh new client quote"
      >
        <Add size={16} />
        <span>Create a New Quote</span>
      </button>
    </div>

    <!-- Scrollable Quotes List -->
    <div class="quotes-list-scroll">
      {#if isLoadingQuotes && quotes.length === 0}
        <div class="loading-state">
          <span class="spinner" />
          <p>Loading database quotes...</p>
        </div>
      {:else if quotesError}
        <div class="error-state">
          <WarningFilled size={24} style="color: var(--tt-danger, #e53935);" />
          <p>{quotesError}</p>
          <button class="btn btn-sm btn-outline" on:click={loadQuotes} type="button">Try Again</button>
        </div>
      {:else if filteredQuotes.length === 0}
        <div class="empty-list-state">
          <p>No quotes match your filter</p>
          {#if selectedStatuses.length === 0}
            <button class="btn btn-xs btn-outline" on:click={resetStatusToDefault} type="button">
              Reset Status Filter
            </button>
          {:else if searchQuery}
            <button class="btn btn-xs btn-ghost" on:click={() => (searchQuery = '')} type="button">
              Clear Search
            </button>
          {/if}
        </div>
      {:else}
        {#each filteredQuotes as q (q.id || q._id)}
          {@const isSelected = ($customerQuote.id && String($customerQuote.id) === String(q.id || q._id)) || ($customerQuote.quoteNumber && q.quote_number && $customerQuote.quoteNumber === q.quote_number)}
          {@const isConverted = (q.status || "").toLowerCase() === "converted" || !!q.job_id || !!q.converted_to_job}
          {@const hasMats = Array.isArray(q.quote_materials) && q.quote_materials.length > 0}
          <button
            class="quote-card {isSelected ? 'selected' : ''}"
            on:click={() => handleSelectQuote(q)}
            type="button"
          >
            <div class="card-top-row">
              <span class="quote-number-pill">
                {q.quote_number || `QT-${q.id || q._id}`}
              </span>
              {#if isConverted || (q.status || "").toLowerCase() === "converted"}
                <span class="status-pill status-converted"><Checkmark size={12} /> Converted</span>
              {:else if (q.status || "").toLowerCase() === "accepted"}
                <span class="status-pill status-accepted"><Checkmark size={12} /> Accepted</span>
              {:else if (q.status || "").toLowerCase() === "sent"}
                <span class="status-pill status-sent">Sent</span>
              {:else if (q.status || "").toLowerCase() === "declined"}
                <span class="status-pill status-declined">Declined</span>
              {:else if hasMats}
                <span class="status-pill status-ready">Ready</span>
              {:else}
                <span class="status-pill status-draft">Draft</span>
              {/if}
            </div>

            <div class="card-client-row">
              <span class="client-name">{getQuoteCustomerName(q)}</span>
            </div>

            <div class="card-site-preview">
              <span class="site-text">{getQuoteSiteAddress(q)}</span>
            </div>

            <div class="card-materials-chips">
              {#each (q.quote_materials || []).slice(0, 3) as mat}
                <span class="mat-chip">{mat.material_name || mat.name || "Material"} ({mat.quantity || mat.total_tonnes || 0}t)</span>
              {/each}
              {#if (q.quote_materials || []).length > 3}
                <span class="mat-chip-more">+{(q.quote_materials || []).length - 3}</span>
              {/if}
            </div>

            <div class="card-footer-row">
              <div class="metric-pair">
                <span class="metric-label">Tonnes:</span>
                <span class="metric-val">{formatTonnes(getQuoteTotalTonnes(q))}</span>
              </div>
              <div class="metric-pair">
                <span class="metric-label">Total (inc GST):</span>
                <span class="metric-val total-val">{formatCurrency(getQuoteGrandTotal(q))}</span>
              </div>
            </div>
          </button>
        {/each}
      {/if}
    </div>
  </aside>

  <!-- Edge Slider Handle Button -->
  <button
    class="sidebar-edge-tab {isSidebarOpen ? 'is-open' : 'is-closed'}"
    on:click={toggleSidebar}
    title={isSidebarOpen ? "Slide quotes list out (hide)" : "Slide quotes list in (show)"}
    type="button"
    aria-label="Toggle quotes sidebar"
  >
    <svg
      class="clipboard-tab-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      width="14"
      height="14"
      aria-hidden="true"
    >
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" fill="currentColor" fill-opacity="0.25" />
      <path d="M9 11h6M9 15h6" />
    </svg>
    {#if isSidebarOpen}
      <ChevronLeft size={13} class="tab-chevron" />
    {:else}
      <ChevronRight size={13} class="tab-chevron" />
    {/if}
  </button>

  <!-- Right Column: Spacious Workbench Studio -->
  <main class="workbench-main">
    <div class="workbench-card">
      <!-- 1. Workbench Header -->
      <div class="workbench-header">
        <div class="header-meta">
          <div class="header-tags">
            <span class="quote-id-large">Quote #{$customerQuote.quoteNumber || ($customerQuote.id ? `QT-${$customerQuote.id}` : "Draft")}</span>
            {#if $customerQuote.id}
              <span class="quote-code-tag">ID #{$customerQuote.id}</span>
            {/if}
            {#if ($customerQuote.status || "").toLowerCase() === "converted"}
              <span class="status-pill status-converted"><Checkmark size={13} /> CONVERTED</span>
            {:else if ($customerQuote.status || "").toLowerCase() === "accepted"}
              <span class="status-pill status-accepted"><Checkmark size={13} /> ACCEPTED</span>
            {:else if ($customerQuote.status || "").toLowerCase() === "sent"}
              <span class="status-pill status-sent">SENT</span>
            {:else if ($customerQuote.status || "").toLowerCase() === "declined"}
              <span class="status-pill status-declined">DECLINED</span>
            {:else if hasHaulage}
              <span class="status-pill status-ready"><Checkmark size={13} /> READY</span>
            {:else}
              <span class="status-pill status-draft">DRAFT</span>
            {/if}
          </div>
          <h2 class="workbench-client-title">
            {$customerQuote.customerName || selectedCompany?.name || "New Client Quote Proposal"}
          </h2>
          <span class="workbench-date">
            Issued: {$customerQuote.quoteDate || "Today"} | Valid until: {$customerQuote.validUntil || "30 Days"} | {packages.length} Material Package{packages.length !== 1 ? 's' : ''} Attached
          </span>
        </div>

        <div class="workbench-header-right">
          <div class="workbench-header-actions">
            <button
              class="btn btn-sm btn-outline"
              on:click={handleOpenInCalculate}
              type="button"
              title="Open and edit this quote in the Calculate tab"
            >
              <Calculator size={15} />
              <span>Open in Calculate</span>
            </button>
            <button class="btn btn-sm btn-outline" on:click={copyQuoteSummary} disabled={!isCompanySelected} title={!isCompanySelected ? "Please select a company first" : "Copy formal quote to clipboard"} type="button">
              {#if copyFeedback}
                <Checkmark size={15} style="color: var(--tt-success, #16a34a);" />
                <span>Copied!</span>
              {:else}
                <Copy size={15} />
                <span>Copy</span>
              {/if}
            </button>
            <button class="btn btn-sm btn-outline" on:click={printQuote} disabled={!isCompanySelected} title={!isCompanySelected ? "Please select a company first" : "Download official PDF quote"} type="button">
              {#if pdfFeedback}
                <Checkmark size={15} style="color: var(--tt-success, #16a34a);" />
                <span>Generating...</span>
              {:else}
                <Printer size={15} />
                <span>PDF Quote</span>
              {/if}
            </button>
            <button class="btn btn-sm btn-primary" on:click={handleSaveQuote} disabled={!isCompanySelected || isSavingQuote} title={!isCompanySelected ? "Please select a company before saving quote" : "Save quote to database"} type="button">
              {#if isSavingQuote}
                <span class="btn-spinner" />
                <span>Saving...</span>
              {:else}
                <Save size={15} />
                <span>Save Quote</span>
              {/if}
            </button>
            <button class="btn btn-sm btn-success" on:click={handleCreateJobFromQuote} disabled={!isCompanySelected || isCreatingJob || isSavingQuote} title={!isCompanySelected ? "Please select a company before creating a job" : "Create Job from quote"} type="button">
              {#if isCreatingJob}
                <span class="btn-spinner" />
                <span>Creating Job...</span>
              {:else}
                <DeliveryTruck size={15} />
                <span>Create Job</span>
              {/if}
            </button>
          </div>

          <!-- Status Dropdown Area (Directly below Save Quote and Create Job) -->
          <div class="header-status-row">
            <label for="quote-status-select" class="status-select-label">Set Status:</label>
            <div class="status-select-wrap">
              <select
                id="quote-status-select"
                class="status-select status-select-{$customerQuote.status || 'draft'}"
                value={$customerQuote.status || 'draft'}
                on:change={handleStatusChange}
              >
                <option value="draft">draft</option>
                <option value="sent">sent</option>
                <option value="accepted">accepted</option>
                <option value="declined">declined</option>
                {#if ($customerQuote.status || '').toLowerCase() === 'converted'}
                  <option value="converted">converted</option>
                {/if}
              </select>
              <ChevronDown size={14} class="select-chevron-icon" />
            </div>
          </div>
        </div>
      </div>

      <!-- Feedback Banner -->
      {#if saveFeedbackMessage}
        <div class="alert {saveFeedbackType === 'success' ? 'alert-success' : 'alert-danger'}">
          {#if saveFeedbackType === 'success'}
            <Checkmark size={18} />
          {:else}
            <WarningFilled size={18} />
          {/if}
          <div class="alert-content">
            <strong>{saveFeedbackType === 'success' ? 'Success:' : 'Notice:'}</strong>
            <span>{saveFeedbackMessage}</span>
          </div>
        </div>
      {/if}

      {#if deleteQuoteSuccessNotice}
        <div class="alert alert-danger" transition:slide>
          <TrashCan size={18} />
          <div class="alert-content">
            <strong>Quote Deleted:</strong>
            <span>{deleteQuoteSuccessNotice}</span>
          </div>
          <button class="banner-close-btn" type="button" on:click={() => (deleteQuoteSuccessNotice = "")} title="Dismiss">
            <Close size={16} />
          </button>
        </div>
      {/if}

      <div class="workbench-body">
        <!-- 2. Two-Column Details Grid: Client & Quote Timing -->
        <div class="details-grid">
          <!-- Left Info Card: Customer & Contact Details -->
          <div class="info-card">
            <div class="info-card-header">
              <h3 class="card-section-title">Client &amp; Contact Details</h3>
              {#if selectedCompany}
                <button
                  type="button"
                  class="btn-inline-link"
                  on:click={() => (showEditCompanyModal = true)}
                >
                  <Edit size={13} />
                  <span>Edit Company</span>
                </button>
              {/if}
            </div>

            {#if !selectedCompany}
              <div class="company-search-box-wrap">
                <div class="company-search-label-row">
                  <label class="field-label" for="company-search-input">
                    Search Client / Company <span class="required-star" title="Company is mandatory">*</span>
                  </label>
                  <span class="required-pill">Required</span>
                </div>
                <div class="company-search-input-field">
                  <Search size={16} class="company-search-icon" />
                  <input
                    id="company-search-input"
                    type="text"
                    class="form-input has-icon"
                    placeholder={isLoadingCompanies ? "Loading companies from database..." : "Start typing company name, ABN, suburb..."}
                    bind:value={companySearchTerm}
                    on:focus={() => (isCompanySearchFocused = true)}
                    on:input={() => (isCompanySearchFocused = true)}
                  />
                  {#if companySearchTerm}
                    <button
                      type="button"
                      class="clear-input-btn"
                      on:click={() => { companySearchTerm = ""; isCompanySearchFocused = false; }}
                    >
                      <Close size={13} />
                    </button>
                  {/if}
                </div>
                <div class="company-required-hint-text">
                  <span>A company must be selected before saving or exporting this quote.</span>
                </div>

                {#if isCompanySearchFocused && filteredCompanies.length > 0}
                  <div class="company-dropdown" transition:slide={{ duration: 120 }}>
                    {#each filteredCompanies as comp}
                      <!-- svelte-ignore a11y-click-events-have-key-events -->
                      <div class="company-dropdown-item" on:mousedown={() => selectCompany(comp)}>
                        <div class="company-item-main">
                          <strong>{comp.name}</strong>
                          {#if comp.ABN}<span class="abn-tag">ABN {comp.ABN}</span>{/if}
                        </div>
                        <div class="company-item-sub">
                          {#if comp.address}<span>{inlineAddress(comp.address)}</span>{/if}
                          {#if comp.phone}<span>• {formatPhoneNumber(comp.phone)}</span>{/if}
                        </div>
                      </div>
                    {/each}
                  </div>
                {:else if isCompanySearchFocused && companySearchTerm.trim().length > 1}
                  <div class="company-dropdown empty" transition:slide={{ duration: 120 }}>
                    <span>No company matching "{companySearchTerm}"</span>
                    <button
                      type="button"
                      class="btn-add-company"
                      on:mousedown={() => handleCreateNewCompany(companySearchTerm.trim())}
                    >
                      + Add "{companySearchTerm.trim()}" to Company Database
                    </button>
                  </div>
                {/if}
              </div>
            {:else}
              <!-- Selected Company Preview Table -->
              <div class="info-table mb-12">
                <div class="info-row">
                  <span class="info-label">Company:</span>
                  <div class="info-val-with-action">
                    <strong class="val-highlight">{selectedCompany.name}</strong>
                    <button type="button" class="btn-change-text" on:click={clearSelectedCompany}>Change</button>
                  </div>
                </div>
                {#if selectedCompany.ABN || selectedCompany.ACN}
                  <div class="info-row">
                    <span class="info-label">ABN / ACN:</span>
                    <span class="info-value">{selectedCompany.ABN || selectedCompany.ACN || "—"}</span>
                  </div>
                {/if}
                {#if selectedCompany.address}
                  <div class="info-row">
                    <span class="info-label">Office Address:</span>
                    <span class="info-value">{inlineAddress(selectedCompany.address) || "—"}</span>
                  </div>
                {/if}
              </div>
            {/if}

            <!-- Prepared For / Contact Person -->
            <div class="form-group mb-12">
              <div class="prepared-label-row">
                <label class="field-label" for="prepared-for-input">Prepared For (Contact Person)</label>
                {#if exactContactMatch}
                  <span class="badge-matched">✓ Matched Company Contact</span>
                {/if}
              </div>
              <div class="input-with-icon-wrap">
                <User size={15} class="field-icon" />
                <input
                  id="prepared-for-input"
                  type="text"
                  class="form-input has-icon"
                  disabled={!selectedCompany}
                  placeholder={selectedCompany ? "Start typing contact person's name..." : "Select a company above first to choose contacts"}
                  bind:value={preparedForInput}
                  on:focus={() => (isPreparedForFocused = true)}
                  on:input={() => (isPreparedForFocused = true)}
                  on:keydown={handlePreparedForKeyDown}
                  on:blur={handlePreparedForBlur}
                />
                {#if preparedForInput}
                  <button
                    type="button"
                    tabindex="-1"
                    class="clear-input-btn"
                    on:click={() => {
                      preparedForInput = "";
                      $customerQuote.contactPerson = "";
                      $customerQuote.prepared_for = "";
                      $customerQuote.phone = "";
                      $customerQuote.email = "";
                      $customerQuote.position = "";
                      isPreparedForFocused = false;
                    }}
                  >
                    <Close size={13} />
                  </button>
                {/if}
              </div>

              <!-- Contacts Autocomplete Dropdown -->
              {#if isPreparedForFocused && selectedCompany}
                <div class="contacts-dropdown-list" transition:slide={{ duration: 120 }}>
                  {#if filteredContacts.length > 0}
                    <div class="dropdown-header">Company Contacts for {selectedCompany.name}</div>
                    {#each filteredContacts as c}
                      <!-- svelte-ignore a11y-click-events-have-key-events -->
                      <div
                        class="contact-row {c.name?.toLowerCase() === preparedForInput.toLowerCase() ? 'active' : ''}"
                        on:mousedown={() => selectContact(c)}
                      >
                        <div class="contact-row-main">
                          <strong>{c.name}</strong>
                          {#if c.position}<span class="pos-badge">{c.position}</span>{/if}
                          {#if c.main}<span class="primary-badge">Primary</span>{/if}
                        </div>
                        <div class="contact-row-sub">
                          {#if c.phone}<span>{formatPhoneNumber(c.phone)}</span>{/if}
                          {#if c.email}<span>• {c.email}</span>{/if}
                        </div>
                      </div>
                    {/each}
                  {/if}
                  {#if preparedForInput.trim().length > 0 && !exactContactMatch}
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <div
                      class="add-contact-row"
                      on:mousedown={() => {
                        isPreparedForFocused = false;
                        promptSaveContactDetails(preparedForInput.trim());
                      }}
                    >
                      <span class="plus-circle">+</span>
                      <div class="add-contact-info">
                        <strong>Add "{preparedForInput.trim()}" to Company Contacts</strong>
                        <span>Save phone, email and position for future quotes</span>
                      </div>
                    </div>
                  {/if}
                </div>
              {/if}
            </div>

            <!-- Contact Phone & Email (2-Columns) -->
            <div class="form-row-2">
              <div class="form-group">
                <label class="field-label" for="contact-phone">Contact Phone</label>
                <PhoneInput
                  id="contact-phone"
                  bind:value={$customerQuote.phone}
                  placeholder="0412 345 678"
                />
              </div>
              <div class="form-group">
                <label class="field-label" for="contact-email">Contact Email</label>
                <input
                  id="contact-email"
                  type="email"
                  class="form-input"
                  placeholder="contact@company.com.au"
                  bind:value={$customerQuote.email}
                />
              </div>
            </div>
          </div>

          <!-- Right Info Card: Quote Schedule & Delivery Specifications -->
          <div class="info-card">
            <h3 class="card-section-title">Quote Schedule &amp; Delivery Specifications</h3>

            <!-- Quote Date & Valid Until (2-Columns) -->
            <div class="form-row-2 mb-12">
              <div class="form-group">
                <label class="field-label" for="quote-date">Quote Issue Date</label>
                <input
                  id="quote-date"
                  type="date"
                  class="form-input"
                  bind:value={$customerQuote.quoteDate}
                />
              </div>
              <div class="form-group">
                <label class="field-label" for="quote-valid-until">Valid Until</label>
                <input
                  id="quote-valid-until"
                  type="date"
                  class="form-input"
                  bind:value={$customerQuote.validUntil}
                />
              </div>
            </div>

            <!-- Payment Terms -->
            <div class="form-group mb-12">
              <div class="field-label-row">
                <label class="field-label mb-0" for="quote-payment-terms">Payment Terms</label>
                <span class="terms-preset-label">Invoice Credit Terms</span>
              </div>
              <div class="input-with-icon-wrap">
                <Receipt size={16} class="field-icon" />
                <input
                  id="quote-payment-terms"
                  type="text"
                  list="payment-terms-presets"
                  class="form-input has-icon"
                  placeholder="e.g. Strictly 14 Days from date of invoice"
                  bind:value={$customerQuote.paymentTerms}
                  on:input={() => {
                    $customerQuote.payment_terms = $customerQuote.paymentTerms;
                  }}
                />
                <datalist id="payment-terms-presets">
                  <option value="Strictly 14 Days from date of invoice" />
                  <option value="Strictly 7 Days from date of invoice" />
                  <option value="Strictly 30 Days from date of invoice" />
                  <option value="30 Days End of Month (EOM)" />
                  <option value="COD (Cash / Payment on Delivery)" />
                  <option value="Prepaid in Advance" />
                  <option value="Approved 14-Day Account" />
                  <option value="Approved 30-Day Account" />
                </datalist>
              </div>
              <div class="terms-quick-pills">
                <button
                  type="button"
                  class="terms-pill {$customerQuote.paymentTerms === 'Strictly 14 Days from date of invoice' || $customerQuote.paymentTerms === '14 Days' ? 'active' : ''}"
                  on:click={() => { $customerQuote.paymentTerms = "Strictly 14 Days from date of invoice"; $customerQuote.payment_terms = $customerQuote.paymentTerms; }}
                >
                  14 Days
                </button>
                <button
                  type="button"
                  class="terms-pill {$customerQuote.paymentTerms === 'Strictly 30 Days from date of invoice' || $customerQuote.paymentTerms === '30 Days' ? 'active' : ''}"
                  on:click={() => { $customerQuote.paymentTerms = "Strictly 30 Days from date of invoice"; $customerQuote.payment_terms = $customerQuote.paymentTerms; }}
                >
                  30 Days
                </button>
                <button
                  type="button"
                  class="terms-pill {$customerQuote.paymentTerms === '30 Days End of Month (EOM)' ? 'active' : ''}"
                  on:click={() => { $customerQuote.paymentTerms = "30 Days End of Month (EOM)"; $customerQuote.payment_terms = $customerQuote.paymentTerms; }}
                >
                  30 Days EOM
                </button>
                <button
                  type="button"
                  class="terms-pill {$customerQuote.paymentTerms === 'Strictly 7 Days from date of invoice' || $customerQuote.paymentTerms === '7 Days' ? 'active' : ''}"
                  on:click={() => { $customerQuote.paymentTerms = "Strictly 7 Days from date of invoice"; $customerQuote.payment_terms = $customerQuote.paymentTerms; }}
                >
                  7 Days
                </button>
                <button
                  type="button"
                  class="terms-pill {$customerQuote.paymentTerms === 'COD (Cash / Payment on Delivery)' || $customerQuote.paymentTerms === 'COD' ? 'active' : ''}"
                  on:click={() => { $customerQuote.paymentTerms = "COD (Cash / Payment on Delivery)"; $customerQuote.payment_terms = $customerQuote.paymentTerms; }}
                >
                  COD
                </button>
              </div>
            </div>

            <!-- Quote Terms / Special Instructions -->
            <div class="form-group">
              <label class="field-label" for="quote-notes">Quote Terms, Operational Hours &amp; Tipping Rules</label>
              <textarea
                id="quote-notes"
                class="form-textarea"
                rows="3"
                bind:value={$customerQuote.notes}
                placeholder="Rates based on standard operating hours (07:00 - 17:00). Tolls and disposal fees included. Subject to clean tipping guidelines..."
              ></textarea>
            </div>
          </div>
        </div>

        <!-- 3. Financial & Operational Summary Strip (4 Columns) -->
        <div class="financial-summary-strip">
          <div class="summary-metric">
            <span class="sm-label">Total Volume</span>
            <span class="sm-val">{formatTonnes(combinedFinancials.totalTonnes)}</span>
          </div>
          <div class="summary-metric">
            <span class="sm-label">Quoted Packages</span>
            <span class="sm-val">{packages.length} Package{packages.length !== 1 ? 's' : ''}</span>
          </div>
          <div class="summary-metric">
            <span class="sm-label">Applied Profit Margin</span>
            <span class="sm-val">{currentMargins?.totalCost?.value || $customerQuote.marginPercent || 15}%</span>
          </div>
          <div class="summary-metric highlight">
            <span class="sm-label">Grand Total (inc GST)</span>
            <span class="sm-val">{formatCurrency(combinedFinancials.totalIncGst || totalIncGst)}</span>
          </div>
        </div>

        <!-- 4. Material Packages Studio (Full Width) -->
        <div class="materials-studio-card">
          <div class="studio-header">
            <div class="studio-header-left">
              <h3 class="card-section-title no-border">Quoted Material Packages</h3>
              <span class="count-tag">{packages.length} Package{packages.length !== 1 ? 's' : ''}</span>
            </div>
            <div class="studio-header-actions">
              <button type="button" class="btn btn-xs btn-outline" on:click={addMaterialPackage}>
                <Add size={14} />
                <span>Add Material Package</span>
              </button>
            </div>
          </div>

          <!-- Material Package Switcher Tabs -->
          <div class="package-tabs-bar">
            {#each packages as p, idx (p.id || idx)}
              {@const pFin = calculatePackageFinancials(p)}
              {@const isActive = (p.id === activePkg?.id) || (!activePkg && idx === 0)}
              <div
                class="pkg-tab-item {isActive ? 'active' : ''}"
                on:click={() => switchTab(p)}
                role="button"
                tabindex="0"
                on:keydown={(e) => { if (e.key === "Enter") switchTab(p); }}
              >
                <span class="tab-pkg-indicator"></span>
                <span class="tab-pkg-name">{p.material || `Material ${idx + 1}`}</span>
                {#if pFin}
                  <span class="tab-pkg-price">{formatCurrency(pFin.totalIncGst)}</span>
                {/if}
                {#if packages.length > 1}
                  <button
                    type="button"
                    class="tab-close-btn"
                    title="Remove package"
                    on:click|stopPropagation={() => removePackage(p.id)}
                  >
                    &times;
                  </button>
                {/if}
              </div>
            {/each}
          </div>

          <!-- Active Package Studio Body -->
          {#if hasHaulage && pkg}
            <div class="active-pkg-studio">
              <div class="pkg-studio-grid">
                <!-- Left Studio Card: Site Details -->
                <div class="studio-sub-card">
                  <!-- Section Header -->
                  <div class="allocation-board-header">
                    <div class="header-main-meta">
                      <div class="badge-title-group">
                        <span class="board-badge">
                          <Location size={14} />
                          <span>Location &amp; Disposal</span>
                        </span>
                        <h3 class="board-title">Site Details</h3>
                      </div>
                      <p class="board-subtitle">
                        Specify loading site location, disposal facility, and contact details for this material.
                      </p>
                    </div>
                  </div>

                  <!-- Material Dispatch Locations & Disposal Yard Panel -->
                  <div class="material-dispatch-info-card">
                    <!-- Route Locations Row (Load Address & Disposal Address) -->
                    <div class="locations-row">
                      <!-- Load Address -->
                      <div class="location-box load-box">
                        <div class="location-header">
                          <span class="location-icon-badge load-badge">
                            <Location size={13} />
                            <span>LOAD ADDRESS</span>
                          </span>
                          <span class="address-req-tag">Street &amp; Suburb Required</span>
                        </div>
                        <div class="geocoder-embed-wrap">
                          {#key `${pkg?.id || $customerQuote.activeMaterialTab || 'load'}-geo`}
                            <Geocoder
                              accessToken={MAPBOX_TOKEN}
                              on:result={handlePkgLoadAddressResult}
                              on:clear={handlePkgLoadAddressClear}
                              value={pkgLoadAddressStr}
                              geocoder
                              options={{
                                countries: "AU",
                                types: "address",
                                placeholder: "Search site address (e.g. Church St, Parramatta)...",
                              }}
                            />
                          {/key}
                        </div>
                        {#if loadAddressWarning}
                          <div class="address-geo-warning">
                            <WarningFilled size={13} />
                            <span>{loadAddressWarning}</span>
                          </div>
                        {/if}
                      </div>

                      <!-- Route Connector Arrow -->
                      <div class="route-arrow-divider" aria-hidden="true" title="Haulage Route Direction">
                        <ArrowRight size={16} />
                      </div>

                      <!-- Disposal Address -->
                      <div class="location-box disposal-box">
                        <div class="location-header">
                          <span class="location-icon-badge disposal-badge">
                            <Location size={13} />
                            <span>DISPOSAL ADDRESS</span>
                          </span>
                          <span class="address-req-tag">Street &amp; Suburb Required</span>
                        </div>
                        <div class="geocoder-embed-wrap">
                          {#key `${pkg?.id || $customerQuote.activeMaterialTab || 'disp'}-geo`}
                            <Geocoder
                              accessToken={MAPBOX_TOKEN}
                              on:result={handlePkgDisposalAddressResult}
                              on:clear={handlePkgDisposalAddressClear}
                              value={pkgDisposalAddressStr}
                              geocoder
                              options={{
                                countries: "AU",
                                types: "address",
                                placeholder: "Search disposal address (e.g. 50 Wallgrove Rd, Eastern Creek)...",
                              }}
                            />
                          {/key}
                        </div>
                        {#if disposalAddressWarning}
                          <div class="address-geo-warning">
                            <WarningFilled size={13} />
                            <span>{disposalAddressWarning}</span>
                          </div>
                        {/if}
                      </div>
                    </div>

                    <!-- Disposal Yard & Contact Details Row -->
                    <div class="yard-info-row">
                      <!-- Yard Brand Identity (Logo + Name + Quick Yard Select) -->
                      <div class="yard-brand-group">
                        <div class="yard-logo-container">
                          {#if resolvedPkgYard?.image}
                            <img
                              class="yard-logo-image"
                              src={"./assests/yards/" + resolvedPkgYard.image}
                              alt={resolvedPkgYard.name || "Disposal Yard"}
                              on:error={(e) => {
                                reportYardImageError(resolvedPkgYard, "not_found", "./assests/yards/" + resolvedPkgYard.image);
                                e.currentTarget.style.display = "none";
                                const fb = e.currentTarget.parentElement.querySelector(".yard-logo-placeholder");
                                if (fb) fb.style.display = "flex";
                              }}
                            />
                            <div class="yard-logo-image yard-logo-placeholder" style="display: none;">🏭</div>
                          {:else}
                            <div class="yard-logo-image yard-logo-placeholder" style="display: flex;">🏭</div>
                          {/if}
                        </div>
                        <div class="yard-name-info">
                          <div class="yard-header-line">
                            <span class="yard-badge-label">
                              <Enterprise size={11} />
                              <span>DISPOSAL YARD</span>
                            </span>
                            <select
                              class="yard-quick-select"
                              title="Select registered yard"
                              value={activePkg?.yardId ?? resolvedPkgYard?.id ?? ""}
                              on:change={handlePkgYardSelectChange}
                            >
                              <option value="">-- Pick Registered Yard --</option>
                              {#each $yards || [] as y}
                                <option value={y.id}>{y.name} {y.suburb || y.address?.suburb ? `(${y.suburb || y.address?.suburb})` : ""}</option>
                              {/each}
                              <option value="custom">✏️ Custom Yard (Manual)</option>
                            </select>
                          </div>
                          <div class="yard-inputs-row">
                            <input
                              type="text"
                              class="site-detail-input yard-name-input"
                              placeholder="Yard / Facility Name"
                              value={activePkg?.yardName || resolvedPkgYard?.name || ""}
                              on:input={(e) => handlePkgYardNameInput(e.target.value)}
                            />
                            <input
                              type="text"
                              class="site-detail-input yard-suburb-input"
                              placeholder="Suburb (e.g. St Marys)"
                              value={activePkg?.suburb || resolvedPkgYard?.suburb || ""}
                              on:input={(e) => handlePkgYardSuburbInput(e.target.value)}
                            />
                          </div>
                        </div>
                      </div>

                      <!-- Main Contact Person -->
                      <div class="yard-contact-card">
                        <div class="contact-header">
                          <span class="contact-badge-label">
                            <User size={11} />
                            <span>MAIN CONTACT</span>
                          </span>
                          <input
                            type="text"
                            class="site-detail-input contact-position-input"
                            placeholder="Role (e.g. Weighbridge)"
                            value={pkgYardContactPosition}
                            on:input={(e) => updatePkgContactField("position", e.target.value)}
                          />
                        </div>
                        <div class="contact-body">
                          <div class="contact-input-row">
                            <User size={12} class="contact-input-icon" />
                            <input
                              type="text"
                              class="site-detail-input contact-field-input"
                              placeholder="Contact Name (e.g. Steve)"
                              value={pkgYardContactName}
                              on:input={(e) => updatePkgContactField("name", e.target.value)}
                            />
                          </div>
                          <div class="contact-sub-row">
                            <div class="contact-input-row contact-phone-wrap">
                              <Phone size={12} class="contact-input-icon" />
                              <input
                                type="tel"
                                class="site-detail-input contact-field-input"
                                placeholder="Phone (e.g. 0417 458 178)"
                                value={pkgYardContactPhone}
                                on:input={(e) => updatePkgContactField("phone", e.target.value)}
                              />
                            </div>
                            <div class="contact-input-row contact-email-wrap">
                              <Email size={12} class="contact-input-icon" />
                              <input
                                type="email"
                                class="site-detail-input contact-field-input"
                                placeholder="Email (e.g. dispatch@yard.com)"
                                value={pkgYardContactEmail}
                                on:input={(e) => updatePkgContactField("email", e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Right Studio Card: Turnaround Schedule & Fleet Capacities -->
                <div class="studio-sub-card">
                  <!-- Section 1: Operating Hours & Durations (Attachment 2 style) -->
                  <div class="calc-section-title">Operating Hours &amp; Durations</div>
                  <div class="calc-grid-5 mb-12">
                    <div class="form-group">
                      <label class="field-label" for="pkg-total-tonnes">Total Tonnes</label>
                      <input
                        id="pkg-total-tonnes"
                        type="number"
                        class="form-input"
                        value={pkg.totalTonnes != null ? pkg.totalTonnes : (tonnes || 0)}
                        on:input={handleTonnesInput}
                        min="0"
                        step="50"
                        placeholder="0"
                      />
                    </div>
                    <div class="form-group">
                      <label class="field-label" for="pkg-start-time">Start (24h)</label>
                      <input
                        id="pkg-start-time"
                        type="text"
                        class="form-input"
                        value={pkg.operating_hours?.start_time ?? pkg.startTime ?? "07:00"}
                        on:input={handleStartTimeInput}
                        placeholder="07:00"
                      />
                    </div>
                    <div class="form-group">
                      <label class="field-label" for="pkg-finish-time">Finish (24h)</label>
                      <input
                        id="pkg-finish-time"
                        type="text"
                        class="form-input"
                        value={pkg.operating_hours?.finish_time ?? pkg.finishTime ?? "17:00"}
                        on:input={handleFinishTimeInput}
                        placeholder="17:00"
                      />
                    </div>
                    <div class="form-group">
                      <label class="field-label" for="pkg-load-time">Load (mins)</label>
                      <input
                        id="pkg-load-time"
                        type="number"
                        class="form-input"
                        value={pkg.operating_hours?.load_mins ?? pkg.load_mins ?? (pkg.loadTime != null ? pkg.loadTime : 10)}
                        on:input={handleLoadMinsInput}
                        min="0"
                        step="5"
                        placeholder="10"
                      />
                    </div>
                    <div class="form-group">
                      <label class="field-label" for="pkg-tip-time">Tip (mins)</label>
                      <input
                        id="pkg-tip-time"
                        type="number"
                        class="form-input"
                        value={pkg.operating_hours?.tip_mins ?? pkg.tip_mins ?? (pkg.tipTime != null ? pkg.tipTime : 10)}
                        on:input={handleTipMinsInput}
                        min="0"
                        step="5"
                        placeholder="10"
                      />
                    </div>
                  </div>

                  <!-- Section 2: Fleet Specifications & Rates (Attachment 2 style) -->
                  <div class="calc-section-title">Fleet Specifications &amp; Rates</div>
                  <div class="calc-fleet-grid mb-12">
                    <div class="form-group">
                      <label class="field-label" for="pkg-truck-type">Truck Type</label>
                      <div class="select-wrapper">
                        <select
                          id="pkg-truck-type"
                          class="form-select"
                          value={selectedTruckTypeId}
                          on:change={handleTruckTypeChange}
                        >
                          {#each truckTypeOptions as opt}
                            <option value={opt.id}>{opt.text}</option>
                          {/each}
                        </select>
                        <span class="select-chevron-icon"><ChevronDown size={14} /></span>
                      </div>
                    </div>

                    <div class="form-group">
                      <label class="field-label" for="pkg-truck-cap">Truck (t)</label>
                      <input
                        id="pkg-truck-cap"
                        type="number"
                        class="form-input fleet-input"
                        value={pkg.fleet?.truck_capacity_tonne ?? pkg.truckCapacityTonne ?? 19}
                        on:input={handleTruckCapInput}
                        min="0"
                        step="1"
                      />
                    </div>

                    <div class="form-group">
                      <label class="field-label" for="pkg-trailer-type">Trailer</label>
                      <div class="select-wrapper">
                        <select
                          id="pkg-trailer-type"
                          class="form-select"
                          value={selectedTrailerTypeId}
                          on:change={handleTrailerTypeChange}
                        >
                          {#each trailerTypeOptions as opt}
                            <option value={opt.id}>{opt.text}</option>
                          {/each}
                        </select>
                        <span class="select-chevron-icon"><ChevronDown size={14} /></span>
                      </div>
                    </div>

                    <div class="form-group">
                      <label class="field-label" for="pkg-trailer-cap">Trailer (t)</label>
                      <input
                        id="pkg-trailer-cap"
                        type="number"
                        class="form-input fleet-input"
                        value={pkg.fleet?.trailer_capacity_tonne ?? pkg.trailerCapacityTonne ?? 19}
                        on:input={handleTrailerCapInput}
                        min="0"
                        step="1"
                      />
                    </div>

                    <div class="form-group">
                      <label class="field-label" for="pkg-day-rate">Day Rate ($)</label>
                      <input
                        id="pkg-day-rate"
                        type="number"
                        class="form-input fleet-input"
                        value={pkg.fleet?.truck_day_rate ?? pkg.dayRate ?? 1500}
                        on:input={handleDayRateInput}
                        min="0"
                        step="50"
                      />
                    </div>
                  </div>

                  <!-- Section 3: Turnaround Schedule & Fleet Allocation (Attachment 3 + metrics) -->
                  <div class="calc-section-title">Turnaround Schedule &amp; Fleet Allocation</div>
                  <div class="highlight-schedule-section">
                    <div class="turnaround-schedule-grid">
                      <div class="schedule-metric-item">
                        <span class="spec-label">Payload Capacity</span>
                        <span class="spec-value">{truckCapacity} tonnes</span>
                      </div>
                      <div class="schedule-metric-item">
                        <span class="spec-label">Daily Runs</span>
                        <span class="spec-value">{dailyRuns > 0 ? `${dailyRuns} runs/pd` : '—'}</span>
                      </div>
                      <div class="schedule-metric-item">
                        <span class="spec-label">Total Loads</span>
                        <span class="spec-value">{totalLoads > 0 ? `${totalLoads.toLocaleString()} loads` : '—'}</span>
                      </div>
                      <div class="schedule-metric-item days-spec-item">
                        <span class="spec-label">Days</span>
                        <div class="days-display-group">
                          <span class="spec-value days-val-highlight">{calculatedDays > 0 ? formatDays(calculatedDays) : '0 days'}</span>
                          {#if totalDays1Truck > 0 && (allocatedTrucks > 1 || totalDays1Truck !== calculatedDays)}
                            <span class="single-truck-note">({formatDays(totalDays1Truck)} w/ 1 truck)</span>
                          {/if}
                        </div>
                      </div>
                      <div class="schedule-metric-item trucks-spec-item">
                        <span class="spec-label">Allocated Trucks</span>
                        <div class="trucks-stepper-control">
                          <input
                            type="number"
                            class="trucks-stepper-input"
                            min="1"
                            max="50"
                            step="1"
                            value={allocatedTrucks}
                            on:input={handleTrucksInput}
                          />
                          <div class="stepper-arrow-buttons">
                            <button
                              type="button"
                              class="truck-step-btn step-up"
                              on:click={incrementTrucks}
                              title="Increase truck count"
                              aria-label="Increase truck count"
                            >
                              <ChevronUp size={11} />
                            </button>
                            <button
                              type="button"
                              class="truck-step-btn step-down"
                              on:click={decrementTrucks}
                              disabled={allocatedTrucks <= 1}
                              title="Decrease truck count"
                              aria-label="Decrease truck count"
                            >
                              <ChevronDown size={11} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    {#if (pkg.schedule?.total_tolls ?? 0) > 0}
                      <div class="turnaround-tolls-line">
                        <span>Route Tolls: <strong>{formatCurrency(pkg.schedule?.total_tolls ?? 0)}</strong></span>
                      </div>
                    {/if}
                  </div>
                </div>
              </div>
            </div>
          {:else}
            <div class="empty-packages-state">
              <DocumentAdd size={32} />
              <p>No material packages attached yet. Click <strong>+ Add Material Package</strong> above to configure rates and tonnages.</p>
            </div>
          {/if}
        </div>

        <!-- 5. Commercial Pricing & Margin Matrix (Full Width) -->
        <div class="pricing-matrix-card">
          <div class="matrix-card-header">
            <div class="matrix-title-group">
              <h3 class="card-section-title no-border">Commercial Pricing &amp; Margin Matrix</h3>
              <span class="matrix-subtitle">Apply individual field markups or package-level profit margins</span>
            </div>
            <button type="button" class="btn btn-xs btn-outline" on:click={resetMargins}>
              <Renew size={13} />
              <span>Reset Margins</span>
            </button>
          </div>

          <div class="matrix-table-wrap">
            <table class="margin-matrix-table">
              <thead>
                <tr>
                  <th class="col-rate-type">Rate Type</th>
                  <th>Disposal Rate<br /><span class="th-unit">$/tonne</span></th>
                  <th>Cost / Load<br /><span class="th-unit">$/load</span></th>
                  <th>Truck Day Rate<br /><span class="th-unit">$/day</span></th>
                  <th>Total Cartage<br /><span class="th-unit">Subtotal $</span></th>
                  <th>Total Tolls<br /><span class="th-unit">Tolls $</span></th>
                  <th class="col-highlight">Subtotal (ex GST)<br /><span class="th-unit">Total $</span></th>
                </tr>
              </thead>
              <tbody>
                <!-- Row 1: Base Costs -->
                <tr class="matrix-row base-row">
                  <td class="row-header-cell"><span class="badge-base">Base Cost</span></td>
                  <td>{formatCurrency(pkg?.financial_breakdown?.base_disposal_rate ?? baseTonneRate)}/t</td>
                  <td>{formatCurrency(pkg?.financial_breakdown?.base_cost_per_load ?? baseCostPerLoad)}</td>
                  <td>{formatCurrency(pkg?.financial_breakdown?.base_day_rate ?? pkg?.fleet?.truck_day_rate ?? baseDayRate)}</td>
                  <td>{formatCurrency(pkg?.financial_breakdown?.base_cartage_cost ?? baseCartageCost)}</td>
                  <td>{formatCurrency(pkg?.financial_breakdown?.base_tolls ?? pkg?.schedule?.total_tolls ?? baseTolls)}</td>
                  <td class="col-highlight"><strong>{formatCurrency(pkg?.financial_breakdown?.base_total_cost ?? baseTotalCost)}</strong></td>
                </tr>

                <!-- Row 2: Margin Adjustments -->
                <tr class="matrix-row margin-row">
                  <td class="row-header-cell"><span class="badge-margin">Margin (+)</span></td>
                  
                  <!-- Disposal Margin -->
                  <td>
                    <div class="margin-cell-control">
                      <button
                        type="button"
                        class="unit-toggle-pill {mTR?.mode === 'dollar' ? 'is-dollar' : 'is-percent'}"
                        on:click={() => toggleMarginMode('tonneRate')}
                      >
                        <span class="toggle-opt {mTR?.mode === 'percent' ? 'active' : ''}">%</span>
                        <span class="toggle-opt {mTR?.mode === 'dollar' ? 'active' : ''}">$</span>
                      </button>
                      <input
                        type="number"
                        class="matrix-number-input"
                        step={mTR?.mode === 'dollar' ? '1' : '0.5'}
                        bind:value={currentMargins.tonneRate.value}
                        placeholder="0"
                      />
                    </div>
                  </td>

                  <!-- Cost Per Load Margin -->
                  <td>
                    <div class="margin-cell-control">
                      <button
                        type="button"
                        class="unit-toggle-pill {mCPL?.mode === 'dollar' ? 'is-dollar' : 'is-percent'}"
                        on:click={() => toggleMarginMode('costPerLoad')}
                      >
                        <span class="toggle-opt {mCPL?.mode === 'percent' ? 'active' : ''}">%</span>
                        <span class="toggle-opt {mCPL?.mode === 'dollar' ? 'active' : ''}">$</span>
                      </button>
                      <input
                        type="number"
                        class="matrix-number-input"
                        step={mCPL?.mode === 'dollar' ? '1' : '0.5'}
                        bind:value={currentMargins.costPerLoad.value}
                        placeholder="0"
                      />
                    </div>
                  </td>

                  <!-- Day Rate Margin -->
                  <td>
                    <div class="margin-cell-control">
                      <button
                        type="button"
                        class="unit-toggle-pill {mDR?.mode === 'dollar' ? 'is-dollar' : 'is-percent'}"
                        on:click={() => toggleMarginMode('dayRate')}
                      >
                        <span class="toggle-opt {mDR?.mode === 'percent' ? 'active' : ''}">%</span>
                        <span class="toggle-opt {mDR?.mode === 'dollar' ? 'active' : ''}">$</span>
                      </button>
                      <input
                        type="number"
                        class="matrix-number-input"
                        step={mDR?.mode === 'dollar' ? '1' : '0.5'}
                        bind:value={currentMargins.dayRate.value}
                        placeholder="0"
                      />
                    </div>
                  </td>

                  <!-- Cartage Margin -->
                  <td>
                    <div class="margin-cell-control">
                      <button
                        type="button"
                        class="unit-toggle-pill {mCC?.mode === 'dollar' ? 'is-dollar' : 'is-percent'}"
                        on:click={() => toggleMarginMode('cartageCost')}
                      >
                        <span class="toggle-opt {mCC?.mode === 'percent' ? 'active' : ''}">%</span>
                        <span class="toggle-opt {mCC?.mode === 'dollar' ? 'active' : ''}">$</span>
                      </button>
                      <input
                        type="number"
                        class="matrix-number-input"
                        step={mCC?.mode === 'dollar' ? '1' : '0.5'}
                        bind:value={currentMargins.cartageCost.value}
                        placeholder="0"
                      />
                    </div>
                  </td>

                  <!-- Tolls Margin -->
                  <td>
                    <div class="margin-cell-control">
                      <button
                        type="button"
                        class="unit-toggle-pill {mTL?.mode === 'dollar' ? 'is-dollar' : 'is-percent'}"
                        on:click={() => toggleMarginMode('totalTolls')}
                      >
                        <span class="toggle-opt {mTL?.mode === 'percent' ? 'active' : ''}">%</span>
                        <span class="toggle-opt {mTL?.mode === 'dollar' ? 'active' : ''}">$</span>
                      </button>
                      <input
                        type="number"
                        class="matrix-number-input"
                        step={mTL?.mode === 'dollar' ? '1' : '0.5'}
                        bind:value={currentMargins.totalTolls.value}
                        placeholder="0"
                      />
                    </div>
                  </td>

                  <!-- Overall Margin -->
                  <td class="col-highlight">
                    <div class="margin-cell-control">
                      <button
                        type="button"
                        class="unit-toggle-pill {mTC?.mode === 'dollar' ? 'is-dollar' : 'is-percent'}"
                        on:click={() => toggleMarginMode('totalCost')}
                      >
                        <span class="toggle-opt {mTC?.mode === 'percent' ? 'active' : ''}">%</span>
                        <span class="toggle-opt {mTC?.mode === 'dollar' ? 'active' : ''}">$</span>
                      </button>
                      <input
                        type="number"
                        class="matrix-number-input highlight-margin-input"
                        step={mTC?.mode === 'dollar' ? '10' : '1'}
                        bind:value={currentMargins.totalCost.value}
                        placeholder="15"
                      />
                    </div>
                  </td>
                </tr>

                <!-- Row 3: Final Quoted Rates -->
                <tr class="matrix-row quoted-row">
                  <td class="row-header-cell"><strong class="badge-quoted">Quoted Rate</strong></td>
                  <td class="quoted-val">{formatCurrency(pkg?.financial_breakdown?.quoted_disposal_rate ?? adjustedTonneRate)}/t</td>
                  <td class="quoted-val">{formatCurrency(pkg?.financial_breakdown?.quoted_cost_per_load ?? adjustedCostPerLoad)}</td>
                  <td class="quoted-val">{formatCurrency(pkg?.financial_breakdown?.quoted_day_rate ?? adjustedDayRate)}</td>
                  <td class="quoted-val">{formatCurrency(pkg?.financial_breakdown?.quoted_cartage_cost ?? adjustedCartageCost)}</td>
                  <td class="quoted-val">{formatCurrency(pkg?.financial_breakdown?.quoted_tolls ?? adjustedTolls)}</td>
                  <td class="col-highlight quoted-total-val">{formatCurrency(pkg?.financial_breakdown?.subtotal_ex_gst ?? subtotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 5. Grand Financial Summary & Bottom Actions (Full Width) -->
        <div class="quote-totals-card">
          <div class="totals-breakdown-col">
            <h4 class="sub-card-title">Commercial Cost &amp; Margin Breakdown</h4>
            <div class="totals-line-item">
              <span class="line-label">Quoted Disposal Services</span>
              <span class="line-value">{formatCurrency(pkg?.financial_breakdown?.quoted_disposal_rate ? (pkg.financial_breakdown.quoted_disposal_rate * tonnes) : combinedFinancials.disposal)}</span>
            </div>
            <div class="totals-line-item">
              <span class="line-label">Quoted Cartage &amp; Fleet Operations</span>
              <span class="line-value">{formatCurrency(pkg?.financial_breakdown?.quoted_cartage_cost ?? combinedFinancials.cartage)}</span>
            </div>
            <div class="totals-line-item">
              <span class="line-label">Tolls &amp; Route Access Fees</span>
              <span class="line-value">{formatCurrency(pkg?.financial_breakdown?.quoted_tolls ?? combinedFinancials.tolls)}</span>
            </div>
            <div class="totals-line-item margin-line">
              <span class="line-label">Applied Profit Margin</span>
              <span class="line-value margin-val">+{formatCurrency(pkg?.financial_breakdown?.margin_added ?? (combinedMarginAmount || marginAmount))}</span>
            </div>
          </div>

          <div class="totals-summary-col">
            <div class="tax-line-item">
              <span>Subtotal (ex GST)</span>
              <strong>{formatCurrency(pkg?.financial_breakdown?.subtotal_ex_gst ?? combinedFinancials.subtotalExGst ?? subtotal)}</strong>
            </div>
            <div class="tax-line-item">
              <span>GST (10%)</span>
              <strong>{formatCurrency(pkg?.financial_breakdown?.gst ?? combinedFinancials.gst ?? gst)}</strong>
            </div>
            <div class="grand-total-hero">
              <div class="grand-total-meta">
                <span class="gt-label">Grand Total (inc GST)</span>
                <span class="gt-subtitle">Ready for client dispatch &amp; operations</span>
              </div>
              <span class="gt-value">{formatCurrency(pkg?.financial_breakdown?.total_inc_gst ?? combinedFinancials.totalIncGst ?? totalIncGst)}</span>
            </div>
          </div>
        </div>

        <!-- Bottom Quote Action Controls -->
        <div class="quote-page-footer">
          <div class="footer-left">
            <span class="footer-quote-info">
              {#if $customerQuote.id}
                Quote #{$customerQuote.quoteNumber || $customerQuote.id} &bull; Created: {$customerQuote.quoteDate || "Today"}
              {:else}
                New Draft Quote (Unsaved)
              {/if}
            </span>
          </div>
          <div class="footer-right">
            <button
              type="button"
              class="btn btn-danger"
              on:click={() => { deleteQuoteError = ""; isDeleteQuoteModalOpen = true; }}
              title="Permanently delete this quote from the database"
            >
              <TrashCan size={16} />
              <span>Delete Quote</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>
</div>

<!-- Modals -->
{#if showEditCompanyModal}
  <CompanyForm
    company={selectedCompany}
    on:company-saved={handleCompanySaved}
    on:company-closed={() => (showEditCompanyModal = false)}
  />
{/if}

{#if showSaveContactModal}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div use:portal class="modal-scrim" role="presentation" on:click|self={handleUseContactWithoutSaving}>
    <div class="modal-surface" role="dialog" aria-modal="true">
      <div class="modal-header">
        <div class="modal-title-group">
          <User size={20} class="modal-icon" />
          <div>
            <h3 class="modal-title">Save Contact to Company</h3>
            <span class="modal-subtitle">Save <strong>{newContactData.name || "this person"}</strong> to <strong>{selectedCompany?.name || "this company"}</strong>?</span>
          </div>
        </div>
        <button type="button" class="btn-close-modal" on:click={handleUseContactWithoutSaving}><Close size={18} /></button>
      </div>

      <div class="modal-body">
        <div class="form-group mb-12">
          <label class="field-label" for="contact-name-modal">Full Name</label>
          <input id="contact-name-modal" type="text" class="form-input" bind:value={newContactData.name} placeholder="e.g. Sarah Jenkins" />
        </div>
        <div class="form-group mb-12">
          <label class="field-label" for="contact-pos-modal">Position / Role</label>
          <input id="contact-pos-modal" type="text" class="form-input" bind:value={newContactData.position} placeholder="e.g. Project Manager" />
        </div>
        <div class="form-group mb-12">
          <label class="field-label" for="contact-phone-modal">Phone Number</label>
          <PhoneInput id="contact-phone-modal" bind:value={newContactData.phone} placeholder="0412 345 678" />
        </div>
        <div class="form-group">
          <label class="field-label" for="contact-email-modal">Email Address</label>
          <input id="contact-email-modal" type="email" class="form-input" bind:value={newContactData.email} placeholder="s.jenkins@company.com.au" />
        </div>
      </div>

      <div class="modal-footer">
        <button type="button" class="btn btn-outline" on:click={handleUseContactWithoutSaving}>Use Without Saving</button>
        <button type="button" class="btn btn-primary" on:click={handleSaveNewContact}>Save to Company &amp; Select</button>
      </div>
    </div>
  </div>
{/if}

{#if isDeleteQuoteModalOpen}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div
    class="delete-modal-backdrop"
    role="presentation"
    on:click={() => !isDeletingQuote && (isDeleteQuoteModalOpen = false)}
    transition:fade={{ duration: 150 }}
  >
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div
      class="delete-modal-card"
      role="dialog"
      aria-modal="true"
      on:click|stopPropagation
      transition:slide={{ duration: 200 }}
    >
      <div class="delete-modal-header">
        <div class="delete-title-row">
          <WarningFilled size={22} class="danger-header-icon" />
          <h3 class="delete-modal-title">Delete Quote Confirmation</h3>
        </div>
        <button
          class="modal-close-btn"
          type="button"
          on:click={() => !isDeletingQuote && (isDeleteQuoteModalOpen = false)}
          disabled={isDeletingQuote}
          aria-label="Close"
        >
          <Close size={18} />
        </button>
      </div>

      <div class="delete-modal-body">
        <p class="delete-prompt-text">
          Are you sure you want to permanently delete
          <strong>Quote #{$customerQuote.quoteNumber || $customerQuote.id || "Draft"}</strong>
          {#if $customerQuote.customerName}
            for <strong>{$customerQuote.customerName}</strong>
          {/if}?
        </p>

        <div class="delete-warning-box">
          <WarningFilled size={20} class="warning-box-icon" />
          <div class="delete-warning-text">
            <strong>Permanent Action — Cannot Be Recovered</strong>
            <span>Once deleted, this quote cannot be recovered. All associated material packages, site configurations, rates, and schedule parameters will be permanently removed from the database.</span>
          </div>
        </div>

        {#if deleteQuoteError}
          <div class="alert alert-danger" transition:slide>
            <WarningFilled size={18} />
            <span>{deleteQuoteError}</span>
          </div>
        {/if}
      </div>

      <div class="delete-modal-footer">
        <button
          class="btn btn-outline"
          type="button"
          on:click={() => (isDeleteQuoteModalOpen = false)}
          disabled={isDeletingQuote}
        >
          Cancel
        </button>
        <button
          class="btn btn-danger btn-confirm-delete"
          type="button"
          on:click={handleConfirmDeleteQuote}
          disabled={isDeletingQuote}
        >
          {#if isDeletingQuote}
            <span class="btn-spinner" />
            <span>Deleting Quote...</span>
          {:else}
            <TrashCan size={16} />
            <span>Yes, Delete Quote</span>
          {/if}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  /* Root Studio Layout matching Job Details */
  .studio-layout {
    display: flex;
    width: 100vw;
    height: calc(100vh - 56px);
    overflow: hidden;
    background: #f8fafc;
    position: relative;
  }

  /* Quotes Sidebar */
  .quotes-sidebar {
    width: 380px;
    min-width: 380px;
    max-width: 380px;
    background: #ffffff;
    border-right: 1px solid var(--tt-slate-200, #e2e8f0);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex-shrink: 0;
    transition: margin-left 0.28s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.22s ease;
    position: relative;
    z-index: 10;
  }

  .quotes-sidebar.collapsed {
    margin-left: -380px;
    pointer-events: none;
    opacity: 0;
  }

  .sidebar-header {
    padding: 1rem 1.25rem 0.75rem 1.25rem;
    border-bottom: 1px solid var(--tt-slate-200, #e2e8f0);
    background: #ffffff;
    position: relative;
    z-index: 30;
  }

  .sidebar-actions-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.65rem;
  }

  /* Status Multi-Select Filter */
  .status-multiselect-wrap {
    flex: 1;
    position: relative;
    min-width: 0;
  }

  .status-multiselect-trigger {
    width: 100%;
    min-height: 36px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.25rem 0.55rem;
    background: var(--tt-slate-50, #f8fafc);
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
    text-align: left;
  }

  .status-multiselect-trigger:hover {
    background: #ffffff;
    border-color: var(--tt-primary, #2563eb);
  }

  .status-multiselect-trigger.open {
    background: #ffffff;
    border-color: var(--tt-primary, #2563eb);
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.18);
  }

  .trigger-label-group {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }

  :global(.filter-icon) {
    color: var(--tt-slate-500, #64748b);
    flex-shrink: 0;
  }

  .trigger-title {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--tt-slate-600, #475569);
    flex-shrink: 0;
  }

  .trigger-chips-container {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
  }

  .status-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.68rem;
    font-weight: 600;
    padding: 0.1rem 0.35rem;
    border-radius: 4px;
    line-height: 1.2;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .chip-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .chip-text {
    max-width: 70px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .chip-remove {
    background: none;
    border: none;
    padding: 0 1px;
    margin-left: 2px;
    font-size: 0.85rem;
    line-height: 1;
    color: inherit;
    opacity: 0.6;
    cursor: pointer;
    border-radius: 2px;
  }

  .chip-remove:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.1);
  }

  .chip-all {
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
  }

  .chip-more {
    background: var(--tt-slate-100, #f1f5f9);
    color: var(--tt-slate-600, #475569);
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    font-size: 0.65rem;
  }

  .placeholder-text {
    font-size: 0.75rem;
    color: var(--tt-slate-400, #94a3b8);
    font-style: italic;
  }

  .trigger-arrow {
    display: flex;
    align-items: center;
    margin-left: 0.35rem;
    color: var(--tt-slate-400, #94a3b8);
    flex-shrink: 0;
  }

  :global(.chevron) {
    transition: transform 0.2s ease;
  }

  :global(.chevron.rotated) {
    transform: rotate(180deg);
  }

  .status-dropdown-menu {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    min-width: 260px;
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08);
    z-index: 100;
    overflow: hidden;
    animation: dropdownFadeIn 0.15s ease-out;
  }

  @keyframes dropdownFadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .dropdown-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0.75rem;
    background: var(--tt-slate-50, #f8fafc);
    border-bottom: 1px solid var(--tt-slate-200, #e2e8f0);
  }

  .dropdown-heading {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--tt-slate-500, #64748b);
  }

  .dropdown-quick-links {
    display: flex;
    align-items: center;
    gap: 0.3rem;
  }

  .quick-link {
    background: none;
    border: none;
    padding: 0;
    font-size: 0.72rem;
    font-weight: 600;
    color: var(--tt-primary, #2563eb);
    cursor: pointer;
    text-decoration: none;
  }

  .quick-link:hover {
    text-decoration: underline;
  }

  .quick-bullet {
    color: var(--tt-slate-300, #cbd5e1);
    font-size: 0.6rem;
  }

  .dropdown-items-list {
    padding: 0.35rem 0;
    max-height: 220px;
    overflow-y: auto;
  }

  .dropdown-item-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.45rem 0.75rem;
    cursor: pointer;
    transition: background 0.12s ease;
    user-select: none;
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--tt-slate-700, #334155);
  }

  .dropdown-item-row:hover {
    background: var(--tt-slate-100, #f1f5f9);
  }

  .dropdown-item-row.checked {
    background: #f0f7ff;
    color: #1e3a8a;
    font-weight: 600;
  }

  .status-checkbox {
    width: 14px;
    height: 14px;
    accent-color: var(--tt-primary, #2563eb);
    cursor: pointer;
    margin: 0;
  }

  .status-indicator-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .status-name {
    flex: 1;
  }

  .status-item-count {
    font-size: 0.72rem;
    color: var(--tt-slate-400, #94a3b8);
    font-weight: 500;
  }

  .status-item-count.has-items {
    color: var(--tt-slate-600, #475569);
    font-weight: 600;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }

  .sidebar-close-btn {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 6px;
    color: var(--tt-slate-500, #64748b);
    cursor: pointer;
  }

  .sidebar-close-btn:hover {
    background: var(--tt-slate-100, #f1f5f9);
  }

  .search-box {
    position: relative;
    display: flex;
    align-items: center;
  }

  :global(.search-icon) {
    position: absolute;
    left: 0.65rem;
    color: var(--tt-slate-400, #94a3b8);
    pointer-events: none;
  }

  .search-input {
    width: 100%;
    padding: 0.45rem 1.8rem 0.45rem 2.2rem;
    font-size: 0.85rem;
    background: var(--tt-slate-50, #f8fafc);
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 6px;
    outline: none;
  }

  .search-input:focus {
    background: #ffffff;
    border-color: #2563eb;
  }

  .clear-btn {
    position: absolute;
    right: 0.5rem;
    background: none;
    border: none;
    color: var(--tt-slate-400, #94a3b8);
    font-size: 1.1rem;
    cursor: pointer;
  }

  .create-new-quote-btn {
    margin-top: 0.65rem;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    padding: 0.52rem 0.85rem;
    font-size: 0.84rem;
    font-weight: 600;
    color: #ffffff;
    background: #2563eb;
    border: 1px solid #1d4ed8;
    border-radius: 6px;
    cursor: pointer;
    box-shadow: 0 1px 2px rgba(37, 99, 235, 0.15);
    transition: all 0.15s ease;
  }

  .create-new-quote-btn:hover {
    background: #1d4ed8;
    border-color: #1e40af;
    box-shadow: 0 2px 5px rgba(37, 99, 235, 0.25);
  }

  .create-new-quote-btn:active {
    transform: translateY(1px);
  }

  .quotes-list-scroll {
    flex: 1;
    overflow-y: auto;
    padding: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
  }

  .quote-card {
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
    padding: 0.85rem;
    cursor: pointer;
    text-align: left;
    transition: all 0.15s ease;
  }

  .quote-card:hover {
    border-color: #93c5fd;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.06);
    transform: translateY(-1px);
  }

  .quote-card.selected {
    border-color: #2563eb;
    background: #f8fafc;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.25);
  }

  .card-top-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.4rem;
  }

  .quote-number-pill {
    font-family: monospace;
    font-weight: 700;
    font-size: 0.82rem;
    color: #1e3a8a;
    background: #dbeafe;
    padding: 0.15rem 0.45rem;
    border-radius: 4px;
  }

  .status-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 0.15rem 0.45rem;
    border-radius: 4px;
  }

  .status-ready {
    background: #dcfce7;
    color: #15803d;
  }

  .status-draft {
    background: var(--tt-slate-100, #f1f5f9);
    color: var(--tt-slate-700, #334155);
  }

  .status-converted {
    background: #dbeafe;
    color: #1d4ed8;
  }

  .client-name {
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--tt-slate-900, #0f172a);
  }

  .card-site-preview {
    font-size: 0.8rem;
    color: var(--tt-slate-500, #64748b);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: 0.15rem;
  }

  .card-materials-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin-top: 0.45rem;
  }

  .mat-chip {
    font-size: 0.72rem;
    background: var(--tt-slate-100, #f1f5f9);
    color: var(--tt-slate-700, #334155);
    padding: 0.15rem 0.4rem;
    border-radius: 4px;
    font-weight: 500;
  }

  .mat-chip-more {
    font-size: 0.72rem;
    color: var(--tt-slate-400, #94a3b8);
    font-weight: 600;
  }

  .card-footer-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.6rem;
    padding-top: 0.45rem;
    border-top: 1px solid var(--tt-slate-100, #f1f5f9);
    font-size: 0.78rem;
  }

  .metric-pair {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .metric-label {
    color: var(--tt-slate-500, #64748b);
  }

  .metric-val {
    font-weight: 600;
    color: var(--tt-slate-800, #1e293b);
  }

  .total-val {
    color: #0f766e;
    font-weight: 700;
  }

  /* Edge Slider Handle */
  .sidebar-edge-tab {
    position: relative;
    top: 50%;
    transform: translateY(-50%);
    width: 25px;
    height: 52px;
    background: #ffffff;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    border-left: none;
    border-radius: 0 8px 8px 0;
    box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    cursor: pointer;
    z-index: 20;
    color: var(--tt-slate-600, #475569);
    transition: background 0.15s ease, color 0.15s ease;
    margin-left: -1px;
  }

  .sidebar-edge-tab:hover {
    background: #eff6ff;
    color: #2563eb;
  }

  /* Right Workbench */
  .workbench-main {
    flex: 1;
    overflow-y: auto;
    padding: 1.25rem 1.75rem;
    background: #f8fafc;
  }

  .workbench-card {
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 10px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .workbench-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 1.25rem 1.5rem;
    background: #ffffff;
    border-bottom: 1px solid var(--tt-slate-200, #e2e8f0);
  }

  .header-tags {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.35rem;
  }

  .quote-id-large {
    font-size: 0.85rem;
    font-weight: 800;
    background: #eff6ff;
    color: #1d4ed8;
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
    font-family: monospace;
  }

  .quote-code-tag {
    font-size: 0.85rem;
    font-weight: 700;
    color: var(--tt-slate-600, #475569);
  }

  .workbench-client-title {
    font-size: 1.45rem;
    font-weight: 800;
    margin: 0 0 0.2rem 0;
    color: var(--tt-slate-900, #0f172a);
  }

  .workbench-date {
    font-size: 0.8rem;
    color: var(--tt-slate-500, #64748b);
  }

  .workbench-header-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.6rem;
  }

  .workbench-header-actions {
    display: flex;
    align-items: center;
    gap: 0.6rem;
  }

  .header-status-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .status-select-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--tt-slate-500, #64748b);
    white-space: nowrap;
  }

  .status-select-wrap {
    position: relative;
    display: inline-flex;
    align-items: center;
  }

  .status-select {
    appearance: none;
    -webkit-appearance: none;
    padding: 0.35rem 2rem 0.35rem 0.75rem;
    font-size: 0.8125rem;
    font-weight: 700;
    text-transform: capitalize;
    border-radius: 6px;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    background: #ffffff;
    color: var(--tt-slate-800, #1e293b);
    cursor: pointer;
    min-width: 140px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    transition: all 0.15s ease;
  }

  .status-select:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  }

  :global(.status-select-wrap .select-chevron-icon) {
    position: absolute;
    right: 0.6rem;
    pointer-events: none;
    color: var(--tt-slate-500, #64748b);
  }

  .status-select.status-select-draft {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #475569;
  }

  .status-select.status-select-sent {
    background: #f0f9ff;
    border-color: #bae6fd;
    color: #0369a1;
  }

  .status-select.status-select-accepted {
    background: #f0fdf4;
    border-color: #bbf7d0;
    color: #15803d;
  }

  .status-select.status-select-declined {
    background: #fef2f2;
    border-color: #fecaca;
    color: #b91c1c;
  }

  .status-select.status-select-converted {
    background: #eff6ff;
    border-color: #bfdbfe;
    color: #1d4ed8;
  }

  .status-sent {
    background: #e0f2fe;
    color: #0369a1;
  }

  .status-accepted {
    background: #dcfce7;
    color: #15803d;
  }

  .status-declined {
    background: #fee2e2;
    color: #b91c1c;
  }

  .workbench-body {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  /* Two Column Details Grid */
  .details-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem;
  }

  .info-card {
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
    padding: 1.2rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
  }

  .info-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.85rem;
    padding-bottom: 0.45rem;
    border-bottom: 1px solid var(--tt-slate-100, #f1f5f9);
  }

  .card-section-title {
    font-size: 0.92rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--tt-slate-700, #334155);
    margin: 0 0 0.85rem 0;
    padding-bottom: 0.45rem;
    border-bottom: 1px solid var(--tt-slate-100, #f1f5f9);
  }

  .card-section-title.no-border {
    margin: 0;
    padding: 0;
    border: none;
  }

  .btn-inline-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: none;
    border: none;
    color: #2563eb;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
  }

  .btn-inline-link:hover {
    text-decoration: underline;
  }

  /* Form Elements */
  .field-label {
    display: block;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--tt-slate-700, #334155);
    margin-bottom: 0.35rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
  }

  .form-row-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
  }

  .form-input, .form-textarea {
    width: 100%;
    padding: 0.5rem 0.75rem;
    font-size: 0.85rem;
    background: #ffffff;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    border-radius: 6px;
    color: var(--tt-slate-800, #1e293b);
    outline: none;
  }

  .form-input:focus, .form-textarea:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }

  .form-input.has-icon {
    padding-left: 2rem;
  }

  .form-textarea {
    resize: vertical;
  }

  .input-with-icon-wrap {
    position: relative;
    display: flex;
    align-items: center;
  }

  :global(.field-icon) {
    position: absolute;
    left: 0.65rem;
    color: var(--tt-slate-400, #94a3b8);
    pointer-events: none;
  }

  .clear-input-btn {
    position: absolute;
    right: 0.5rem;
    background: none;
    border: none;
    color: var(--tt-slate-400, #94a3b8);
    cursor: pointer;
    display: flex;
  }

  /* Company Autocomplete Dropdown */
  .company-search-box-wrap {
    position: relative;
    margin-bottom: 1rem;
  }

  .company-search-label-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
  }

  .required-star {
    color: #ef4444;
    font-weight: 700;
    margin-left: 2px;
    font-size: 13px;
    line-height: 1;
  }

  .required-pill {
    display: inline-flex;
    align-items: center;
    font-size: 9.5px;
    font-weight: 700;
    color: #dc2626;
    background-color: #fee2e2;
    border: 1px solid #fecaca;
    padding: 1px 5px;
    border-radius: 4px;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    line-height: 1.2;
  }

  .company-required-hint-text {
    font-size: 10.5px;
    color: var(--tt-slate-500, #64748b);
    margin-top: 3px;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .company-search-input-field {
    position: relative;
    display: flex;
    align-items: center;
  }

  :global(.company-search-icon) {
    position: absolute;
    left: 0.65rem;
    color: var(--tt-slate-400, #94a3b8);
    pointer-events: none;
  }

  .company-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    z-index: 100;
    max-height: 220px;
    overflow-y: auto;
  }

  .company-dropdown-item {
    padding: 0.6rem 0.85rem;
    border-bottom: 1px solid var(--tt-slate-100, #f1f5f9);
    cursor: pointer;
  }

  .company-dropdown-item:hover {
    background: #f8fafc;
  }

  .company-item-main {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .abn-tag {
    font-size: 0.72rem;
    background: var(--tt-slate-100, #f1f5f9);
    color: var(--tt-slate-600, #475569);
    padding: 1px 5px;
    border-radius: 4px;
  }

  .company-item-sub {
    font-size: 0.75rem;
    color: var(--tt-slate-500, #64748b);
    margin-top: 0.2rem;
  }

  .company-dropdown.empty {
    padding: 0.85rem;
    text-align: center;
    color: var(--tt-slate-500, #64748b);
    font-size: 0.8rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .btn-add-company {
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
    border-radius: 6px;
    padding: 0.4rem;
    font-weight: 600;
    font-size: 0.8rem;
    cursor: pointer;
  }

  /* Info Table */
  .info-table {
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
  }

  .info-row {
    display: flex;
    font-size: 0.85rem;
    line-height: 1.4;
  }

  .info-label {
    width: 120px;
    color: var(--tt-slate-500, #64748b);
    flex-shrink: 0;
  }

  .info-val-with-action {
    display: flex;
    align-items: center;
    gap: 0.65rem;
  }

  .val-highlight {
    color: var(--tt-slate-900, #0f172a);
    font-weight: 700;
  }

  .btn-change-text {
    background: none;
    border: none;
    color: #2563eb;
    font-size: 0.75rem;
    cursor: pointer;
    font-weight: 600;
    padding: 0;
  }

  .btn-change-text:hover {
    text-decoration: underline;
  }

  /* Prepared For row & badges */
  .prepared-label-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.35rem;
  }

  .badge-matched {
    font-size: 0.72rem;
    font-weight: 700;
    color: #15803d;
    background: #dcfce7;
    padding: 1px 6px;
    border-radius: 4px;
  }

  .contacts-dropdown-list {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    z-index: 100;
    max-height: 220px;
    overflow-y: auto;
  }

  .dropdown-header {
    padding: 0.4rem 0.75rem;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    color: var(--tt-slate-400, #94a3b8);
    background: #f8fafc;
    border-bottom: 1px solid var(--tt-slate-100, #f1f5f9);
  }

  .contact-row {
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid var(--tt-slate-100, #f1f5f9);
    cursor: pointer;
  }

  .contact-row:hover {
    background: #f8fafc;
  }

  .contact-row.active {
    background: #eff6ff;
  }

  .contact-row-main {
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  .pos-badge {
    font-size: 0.7rem;
    background: var(--tt-slate-100, #f1f5f9);
    color: var(--tt-slate-600, #475569);
    padding: 1px 4px;
    border-radius: 3px;
  }

  .primary-badge {
    font-size: 0.68rem;
    background: #dbeafe;
    color: #1d4ed8;
    padding: 1px 5px;
    border-radius: 3px;
    font-weight: 700;
  }

  .contact-row-sub {
    font-size: 0.75rem;
    color: var(--tt-slate-500, #64748b);
    margin-top: 0.15rem;
  }

  .add-contact-row {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    padding: 0.65rem 0.75rem;
    background: #f0fdf4;
    cursor: pointer;
    border-top: 1px solid #bbf7d0;
  }

  .plus-circle {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #16a34a;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.9rem;
  }

  .add-contact-info {
    display: flex;
    flex-direction: column;
    font-size: 0.75rem;
  }

  .add-contact-info strong {
    color: #166534;
  }

  /* 4-Column Financial Metric Strip */
  .financial-summary-strip {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
    padding: 1rem 1.25rem;
  }

  .summary-metric {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    border-right: 1px solid var(--tt-slate-100, #f1f5f9);
    padding-right: 1rem;
  }

  .summary-metric:last-child {
    border-right: none;
    padding-right: 0;
  }

  .sm-label {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--tt-slate-500, #64748b);
  }

  .sm-val {
    font-size: 1.25rem;
    font-weight: 800;
    color: var(--tt-slate-900, #0f172a);
  }

  .summary-metric.highlight .sm-val {
    color: #0f766e;
    font-size: 1.35rem;
  }

  /* Materials Studio Card */
  .materials-studio-card {
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
    overflow: hidden;
  }

  .studio-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid var(--tt-slate-200, #e2e8f0);
    background: #ffffff;
  }

  .studio-header-left {
    display: flex;
    align-items: center;
    gap: 0.65rem;
  }

  .count-tag {
    font-size: 0.72rem;
    font-weight: 700;
    background: var(--tt-slate-100, #f1f5f9);
    color: var(--tt-slate-600, #475569);
    padding: 2px 7px;
    border-radius: 999px;
  }

  /* Package Tabs */
  .package-tabs-bar {
    display: flex;
    overflow-x: auto;
    background: var(--tt-slate-50, #f8fafc);
    border-bottom: 1px solid var(--tt-slate-200, #e2e8f0);
    padding: 0.5rem 1rem 0;
    gap: 0.5rem;
  }

  .pkg-tab-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.55rem 0.9rem;
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-bottom: none;
    border-radius: 6px 6px 0 0;
    cursor: pointer;
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--tt-slate-600, #475569);
    position: relative;
    top: 1px;
    transition: all 0.15s ease;
  }

  .pkg-tab-item:hover {
    color: var(--tt-slate-900, #0f172a);
    background: #ffffff;
  }

  .pkg-tab-item.active {
    background: #ffffff;
    color: #2563eb;
    border-top: 2px solid #2563eb;
    border-color: var(--tt-slate-200, #e2e8f0) var(--tt-slate-200, #e2e8f0) #ffffff var(--tt-slate-200, #e2e8f0);
  }

  .tab-pkg-indicator {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #22c55e;
  }

  .tab-pkg-price {
    font-size: 0.75rem;
    font-weight: 700;
    background: #eff6ff;
    color: #1d4ed8;
    padding: 1px 5px;
    border-radius: 4px;
  }

  .tab-close-btn {
    background: none;
    border: none;
    color: var(--tt-slate-400, #94a3b8);
    font-size: 14px;
    cursor: pointer;
    padding: 0;
  }

  .active-pkg-studio {
    padding: 1.25rem;
  }

  .pkg-studio-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem;
  }

  .studio-sub-card {
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
    padding: 1.15rem;
  }

  .sub-card-title {
    font-size: 0.85rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--tt-slate-700, #334155);
    margin: 0 0 0.85rem 0;
  }

  /* Header for allocation window */
  .allocation-board-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 12px;
  }

  .header-main-meta {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .badge-title-group {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .board-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background-color: rgba(37, 99, 235, 0.1);
    color: var(--tt-primary, #2563eb);
    padding: 3px 8px;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .board-title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--tt-slate-900, #0f172a);
  }

  .board-subtitle {
    margin: 0;
    font-size: 0.82rem;
    color: var(--tt-slate-500, #64748b);
    line-height: 1.4;
    max-width: 780px;
  }

  /* Material Dispatch Info Card (Locations & Disposal Yard) */
  .material-dispatch-info-card {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px 14px;
    background-color: var(--tt-slate-50, #f8fafc);
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: var(--tt-radius-md, 8px);
    margin: 6px 0 0 0;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }

  .locations-row {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .location-box {
    flex: 1;
    min-width: 0;
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 6px;
    padding: 8px 10px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    transition: all 0.15s ease;
  }

  .location-box:hover {
    border-color: var(--tt-slate-300, #cbd5e1);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  }

  .location-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
  }

  .location-icon-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.65rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 2px 6px;
    border-radius: 4px;
  }

  .load-badge {
    color: #047857;
    background-color: #ecfdf5;
    border: 1px solid #a7f3d0;
  }

  .disposal-badge {
    color: #b45309;
    background-color: #fffbeb;
    border: 1px solid #fde68a;
  }

  .address-req-tag {
    font-size: 0.62rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 2px 6px;
    border-radius: 4px;
    background: #eff6ff;
    color: #2563eb;
    border: 1px solid #bfdbfe;
    white-space: nowrap;
  }

  .geocoder-embed-wrap {
    width: 100%;
    position: relative;
  }

  :global(.geocoder-embed-wrap .mapboxgl-ctrl-geocoder) {
    width: 100% !important;
    max-width: 100% !important;
    border: 1px solid #cbd5e1 !important;
    border-radius: 6px !important;
    box-shadow: none !important;
    height: 34px !important;
    min-height: 34px !important;
    font-size: 12.5px !important;
    background-color: #ffffff !important;
    transition: all 0.15s ease !important;
    display: flex !important;
    align-items: center !important;
  }

  :global(.geocoder-embed-wrap .mapboxgl-ctrl-geocoder:focus-within) {
    border-color: #2563eb !important;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15) !important;
  }

  :global(.geocoder-embed-wrap .mapboxgl-ctrl-geocoder--input) {
    height: 32px !important;
    padding: 0 28px 0 30px !important;
    font-size: 12px !important;
    color: #1e293b !important;
  }

  :global(.geocoder-embed-wrap .mapboxgl-ctrl-geocoder--icon-search) {
    top: 8px !important;
    left: 8px !important;
    fill: #94a3b8 !important;
  }

  :global(.geocoder-embed-wrap .mapboxgl-ctrl-geocoder--button) {
    top: 5px !important;
    right: 5px !important;
    background: transparent !important;
  }

  .address-geo-warning {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.72rem;
    font-weight: 500;
    color: #b91c1c;
    background: #fef2f2;
    border: 1px solid #fecaca;
    padding: 4px 8px;
    border-radius: 6px;
    margin-top: 3px;
    line-height: 1.25;
  }

  .route-arrow-divider {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--tt-slate-400, #94a3b8);
    flex-shrink: 0;
  }

  /* Disposal Yard Row */
  .yard-info-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding-top: 10px;
    border-top: 1px dashed var(--tt-slate-200, #e2e8f0);
    flex-wrap: wrap;
  }

  .yard-brand-group {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    min-width: 0;
    flex: 1 1 240px;
  }

  .yard-logo-container {
    width: 44px;
    height: 44px;
    border-radius: 6px;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    overflow: hidden;
    background: #ffffff;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    margin-top: 2px;
  }

  .yard-logo-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .yard-logo-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    background: var(--tt-slate-100, #f1f5f9);
  }

  .yard-name-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: 4px;
    flex: 1;
  }

  .yard-header-line {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
  }

  .yard-badge-label {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 0.62rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--tt-slate-400, #94a3b8);
  }

  .yard-quick-select {
    font-size: 0.7rem;
    font-weight: 500;
    color: #334155;
    background: #ffffff;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    padding: 2px 6px;
    outline: none;
    max-width: 180px;
    cursor: pointer;
    transition: border-color 0.15s ease;
  }

  .yard-quick-select:focus {
    border-color: #2563eb;
  }

  .yard-inputs-row {
    display: flex;
    gap: 6px;
    width: 100%;
  }

  .site-detail-input {
    border: 1px solid #cbd5e1;
    border-radius: 5px;
    padding: 4px 8px;
    font-size: 0.75rem;
    color: #1e293b;
    background: #ffffff;
    outline: none;
    transition: all 0.15s ease;
  }

  .site-detail-input:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.12);
  }

  .yard-name-input {
    flex: 1.4;
    font-weight: 600;
    min-width: 0;
  }

  .yard-suburb-input {
    flex: 1;
    min-width: 0;
  }

  .yard-contact-card {
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 6px;
    padding: 6px 10px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 220px;
    flex: 1 1 220px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
  }

  .contact-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
  }

  .contact-badge-label {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 0.62rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #2563eb;
  }

  .contact-position-input {
    font-size: 0.65rem;
    padding: 2px 6px;
    max-width: 120px;
    color: #64748b;
  }

  .contact-body {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .contact-input-row {
    display: flex;
    align-items: center;
    gap: 6px;
    position: relative;
    width: 100%;
  }

  :global(.contact-input-icon) {
    color: #94a3b8;
    flex-shrink: 0;
  }

  .contact-field-input {
    flex: 1;
    min-width: 0;
    height: 26px;
  }

  .contact-sub-row {
    display: flex;
    gap: 6px;
    width: 100%;
  }

  .contact-phone-wrap,
  .contact-email-wrap {
    flex: 1;
    min-width: 0;
  }

  @media (max-width: 600px) {
    .locations-row {
      flex-direction: column;
      align-items: stretch;
    }

    .route-arrow-divider {
      transform: rotate(90deg);
      padding: 2px 0;
    }
  }

  .field-label-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 0.35rem;
  }

  .field-label.mb-0 {
    margin-bottom: 0 !important;
  }

  .site-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 2px 6px;
    border-radius: 4px;
  }

  .site-status-badge.valid {
    color: #15803d;
    background: #dcfce7;
    border: 1px solid #bbf7d0;
  }

  .site-status-badge.invalid {
    color: #b91c1c;
    background: #fee2e2;
    border: 1px solid #fecaca;
  }

  .input-invalid {
    border-color: #ef4444 !important;
    background-color: #fef2f2 !important;
    box-shadow: 0 0 0 1px #ef4444 !important;
  }

  .address-validation-alert-sm {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.72rem;
    font-weight: 500;
    color: #b91c1c;
    background: #fef2f2;
    border: 1px solid #fecaca;
    padding: 5px 8px;
    border-radius: 6px;
    margin-top: 4px;
    line-height: 1.3;
  }

  .terms-preset-label {
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--tt-slate-400, #94a3b8);
  }

  .terms-quick-pills {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 6px;
    flex-wrap: wrap;
  }

  .terms-pill {
    background: var(--tt-slate-100, #f1f5f9);
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 4px;
    padding: 2px 8px;
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--tt-slate-600, #475569);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .terms-pill:hover {
    background: #e0f2fe;
    color: #0369a1;
    border-color: #bae6fd;
  }

  .terms-pill.active {
    background: #dbeafe;
    color: #1d4ed8;
    border-color: #93c5fd;
    font-weight: 700;
  }

  /* Section Titles matching Attachment 2 */
  .calc-section-title {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--tt-slate-500, #64748b);
    margin: 0.5rem 0 0.4rem 0;
  }

  .calc-section-title:first-child {
    margin-top: 0;
  }

  /* 5-Column Grid for Operating Hours & Durations */
  .calc-grid-5 {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 0.5rem;
    align-items: end;
  }

  /* 5-Column Grid for Fleet Specifications & Rates */
  .calc-fleet-grid {
    display: grid;
    grid-template-columns: 1.7fr 0.9fr 1.7fr 1.2fr 1.5fr;
    gap: 0.5rem;
    align-items: end;
  }

  .fleet-input {
    height: 38px;
  }

  .select-wrapper {
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
  }

  .form-select {
    width: 100%;
    height: 38px;
    padding: 0 1.8rem 0 0.75rem;
    font-size: 0.85rem;
    background: #ffffff;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    border-radius: 6px;
    color: var(--tt-slate-800, #1e293b);
    outline: none;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    cursor: pointer;
  }

  .form-select:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }

  .select-chevron-icon {
    position: absolute;
    right: 0.65rem;
    color: var(--tt-slate-500, #64748b);
    pointer-events: none;
    display: flex;
    align-items: center;
  }

  /* Turnaround Schedule & Fleet Allocation (Attachment 3 style) */
  .highlight-schedule-section {
    background: linear-gradient(135deg, rgba(239, 246, 255, 0.85), rgba(240, 253, 244, 0.85)) !important;
    border: 1px solid #bfdbfe !important;
    border-radius: 6px;
    padding: 0.65rem 0.85rem;
  }

  .turnaround-schedule-grid {
    display: grid;
    grid-template-columns: 1.1fr 1fr 1fr 1.3fr 1.1fr;
    gap: 0.65rem;
    align-items: center;
  }

  .schedule-metric-item {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .spec-label {
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--tt-slate-500, #64748b);
    white-space: nowrap;
  }

  .spec-value {
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--tt-slate-900, #0f172a);
  }

  .days-display-group {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .days-val-highlight {
    color: #1d4ed8 !important;
    font-weight: 800 !important;
    font-size: 1.05rem;
    line-height: 1.2;
  }

  .single-truck-note {
    font-size: 9.5px;
    font-weight: 500;
    color: var(--tt-slate-500, #64748b);
    line-height: 1.1;
    white-space: nowrap;
  }

  /* Stepper Control from Attachment 3 */
  .trucks-stepper-control {
    display: inline-flex;
    align-items: center;
    position: relative;
    width: 68px;
    height: 28px;
    background: #ffffff;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    border-radius: 5px;
    transition: all 0.15s ease;
    overflow: hidden;
    margin-top: 1px;
  }

  .trucks-stepper-control:focus-within {
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }

  .trucks-stepper-input {
    width: 44px;
    height: 100%;
    border: none;
    background: transparent;
    padding: 0 4px;
    font-size: 12px;
    font-weight: 700;
    color: var(--tt-slate-900, #0f172a);
    text-align: center;
    outline: none;
    -moz-appearance: textfield;
  }

  .trucks-stepper-input::-webkit-inner-spin-button,
  .trucks-stepper-input::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .stepper-arrow-buttons {
    display: flex;
    flex-direction: column;
    width: 22px;
    height: 100%;
    border-left: 1px solid var(--tt-slate-200, #e2e8f0);
    position: static;
  }

  .truck-step-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8fafc;
    border: none;
    cursor: pointer;
    color: var(--tt-slate-600, #475569);
    transition: background 0.1s ease;
    padding: 0;
    width: 100%;
    height: 50%;
  }

  .truck-step-btn:first-child {
    border-bottom: 1px solid var(--tt-slate-200, #e2e8f0);
  }

  .truck-step-btn:hover:not(:disabled) {
    background: #e2e8f0;
    color: #1d4ed8;
  }

  .truck-step-btn:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  .turnaround-tolls-line {
    font-size: 0.75rem;
    color: var(--tt-slate-500, #64748b);
    padding-top: 0.35rem;
    border-top: 1px dashed #bfdbfe;
    margin-top: 0.45rem;
  }

  .turnaround-tolls-line strong {
    color: var(--tt-slate-800, #1e293b);
  }

  @media (max-width: 680px) {
    .calc-grid-5 {
      grid-template-columns: repeat(2, 1fr);
    }
    .calc-fleet-grid {
      grid-template-columns: repeat(2, 1fr);
    }
    .turnaround-schedule-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .empty-packages-state {
    padding: 2.5rem 1rem;
    text-align: center;
    color: var(--tt-slate-500, #64748b);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.65rem;
  }

  /* Pricing Matrix Card */
  .pricing-matrix-card {
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
    overflow: hidden;
  }

  .matrix-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid var(--tt-slate-200, #e2e8f0);
  }

  .matrix-subtitle {
    font-size: 0.8rem;
    color: var(--tt-slate-500, #64748b);
  }

  .matrix-table-wrap {
    overflow-x: auto;
  }

  .margin-matrix-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;
  }

  .margin-matrix-table th {
    padding: 0.65rem 0.85rem;
    background: var(--tt-slate-50, #f8fafc);
    color: var(--tt-slate-700, #334155);
    font-weight: 700;
    text-align: right;
    border-bottom: 1px solid var(--tt-slate-200, #e2e8f0);
  }

  .margin-matrix-table th.col-rate-type {
    text-align: left;
    width: 120px;
  }

  .th-unit {
    font-size: 0.7rem;
    font-weight: 500;
    color: var(--tt-slate-500, #64748b);
  }

  .margin-matrix-table td {
    padding: 0.65rem 0.85rem;
    text-align: right;
    border-bottom: 1px solid var(--tt-slate-100, #f1f5f9);
  }

  .row-header-cell {
    text-align: left !important;
  }

  .badge-base {
    font-size: 0.72rem;
    font-weight: 700;
    background: var(--tt-slate-100, #f1f5f9);
    color: var(--tt-slate-700, #334155);
    padding: 2px 7px;
    border-radius: 4px;
  }

  .badge-margin {
    font-size: 0.72rem;
    font-weight: 700;
    background: #dbeafe;
    color: #1d4ed8;
    padding: 2px 7px;
    border-radius: 4px;
  }

  .badge-quoted {
    font-size: 0.72rem;
    font-weight: 800;
    background: #dcfce7;
    color: #15803d;
    padding: 2px 7px;
    border-radius: 4px;
  }

  .col-highlight {
    background: rgba(15, 118, 110, 0.04);
  }

  .quoted-val {
    font-weight: 700;
    color: var(--tt-slate-900, #0f172a);
  }

  .quoted-total-val {
    font-size: 1.05rem;
    font-weight: 800;
    color: #0f766e;
  }

  .margin-cell-control {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    justify-content: flex-end;
  }

  .unit-toggle-pill {
    display: inline-flex;
    background: var(--tt-slate-100, #f1f5f9);
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    border-radius: 4px;
    padding: 1px;
    cursor: pointer;
  }

  .toggle-opt {
    font-size: 10px;
    font-weight: 700;
    padding: 1px 4px;
    color: var(--tt-slate-500, #64748b);
    border-radius: 2px;
  }

  .toggle-opt.active {
    background: #2563eb;
    color: #ffffff;
  }

  .matrix-number-input {
    width: 65px;
    height: 28px;
    padding: 0 4px;
    font-size: 0.8rem;
    text-align: right;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    border-radius: 4px;
  }

  .highlight-margin-input {
    width: 75px;
    border-color: #0f766e;
    font-weight: 700;
  }

  /* Grand Totals Card */
  .quote-totals-card {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
    padding: 1.25rem 1.5rem;
  }

  .totals-breakdown-col {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .totals-line-item {
    display: flex;
    justify-content: space-between;
    font-size: 0.85rem;
    color: var(--tt-slate-600, #475569);
  }

  .totals-line-item.margin-line {
    font-weight: 700;
    color: #1d4ed8;
    padding-top: 0.35rem;
    border-top: 1px solid var(--tt-slate-100, #f1f5f9);
  }

  .totals-summary-col {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .tax-line-item {
    display: flex;
    justify-content: space-between;
    font-size: 0.9rem;
    color: var(--tt-slate-700, #334155);
  }

  .grand-total-hero {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #f0fdf4;
    border: 1px solid #86efac;
    border-radius: 8px;
    padding: 1rem 1.25rem;
    margin-top: 0.5rem;
  }

  .grand-total-meta {
    display: flex;
    flex-direction: column;
  }

  .gt-label {
    font-size: 0.95rem;
    font-weight: 800;
    color: #166534;
  }

  .gt-subtitle {
    font-size: 0.72rem;
    color: #15803d;
  }

  .gt-value {
    font-size: 1.75rem;
    font-weight: 900;
    color: #14532d;
  }

  /* Button System */
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-weight: 600;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
    border: 1px solid transparent;
  }

  .btn-sm {
    padding: 0.4rem 0.75rem;
    font-size: 0.82rem;
  }

  .btn-xs {
    padding: 0.25rem 0.55rem;
    font-size: 0.75rem;
  }

  .btn-primary {
    background: #2563eb;
    color: #ffffff;
  }

  .btn-primary:hover {
    background: #1d4ed8;
  }

  .btn-outline {
    background: #ffffff;
    border-color: var(--tt-slate-300, #cbd5e1);
    color: var(--tt-slate-700, #334155);
  }

  .btn-outline:hover {
    background: var(--tt-slate-50, #f8fafc);
    border-color: var(--tt-slate-400, #94a3b8);
  }

  .btn-success {
    background: #16a34a;
    color: #ffffff;
  }

  .btn-success:hover {
    background: #15803d;
  }

  .btn-danger {
    background: #dc2626;
    color: #ffffff;
    border: 1px solid #b91c1c;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.88rem;
    transition: all 0.15s ease;
  }

  .btn-danger:hover:not(:disabled) {
    background: #b91c1c;
    border-color: #991b1b;
    box-shadow: 0 2px 6px rgba(220, 38, 38, 0.3);
  }

  .btn-danger:active:not(:disabled) {
    transform: translateY(1px);
  }

  .btn-danger:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  .quote-page-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.25rem 1.5rem;
    margin-top: 1.5rem;
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  }

  .footer-quote-info {
    font-size: 0.84rem;
    color: var(--tt-slate-500, #64748b);
    font-weight: 600;
  }

  .quote-page-footer .footer-right {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  /* Delete Modal Styles */
  .delete-modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(15, 23, 42, 0.6);
    backdrop-filter: blur(3px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 1rem;
  }

  .delete-modal-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 520px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
  }

  .delete-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.1rem 1.4rem;
    background: #fef2f2;
    border-bottom: 1px solid #fee2e2;
  }

  .delete-title-row {
    display: flex;
    align-items: center;
    gap: 0.6rem;
  }

  :global(.danger-header-icon) {
    color: #dc2626;
    flex-shrink: 0;
  }

  .delete-modal-title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: #991b1b;
  }

  .delete-modal-body {
    padding: 1.4rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .delete-prompt-text {
    margin: 0;
    font-size: 0.92rem;
    color: var(--tt-slate-700, #334155);
    line-height: 1.5;
  }

  .delete-warning-box {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.9rem 1.1rem;
    background: #fff1f2;
    border: 1px solid #fecdd3;
    border-radius: 8px;
    color: #9f1239;
    font-size: 0.84rem;
    line-height: 1.45;
  }

  :global(.warning-box-icon) {
    color: #e11d48;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .delete-warning-text strong {
    display: block;
    font-weight: 700;
    margin-bottom: 0.2rem;
    color: #be123c;
  }

  .delete-modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1rem 1.4rem;
    background: var(--tt-slate-50, #f8fafc);
    border-top: 1px solid var(--tt-slate-200, #e2e8f0);
  }

  .btn-confirm-delete {
    padding: 0.55rem 1.25rem;
    font-weight: 700;
  }

  .btn-ghost {
    background: transparent;
    color: #2563eb;
  }

  .btn-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }

  /* Modals */
  .modal-scrim {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
  }

  .modal-surface {
    background: #ffffff;
    border-radius: 10px;
    width: 480px;
    max-width: 90vw;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.1rem 1.25rem;
    border-bottom: 1px solid var(--tt-slate-200, #e2e8f0);
  }

  .modal-title-group {
    display: flex;
    align-items: center;
    gap: 0.65rem;
  }

  :global(.modal-icon) {
    color: #2563eb;
  }

  .modal-title {
    font-size: 1rem;
    font-weight: 700;
    margin: 0;
  }

  .modal-subtitle {
    font-size: 0.78rem;
    color: var(--tt-slate-500, #64748b);
  }

  .btn-close-modal {
    background: none;
    border: none;
    color: var(--tt-slate-400, #94a3b8);
    cursor: pointer;
  }

  .modal-body {
    padding: 1.25rem;
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.65rem;
    padding: 1rem 1.25rem;
    background: var(--tt-slate-50, #f8fafc);
    border-top: 1px solid var(--tt-slate-200, #e2e8f0);
  }

  /* Alerts */
  .alert {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.85rem 1.25rem;
    border-radius: 8px;
    font-size: 0.85rem;
  }

  .alert-success {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    color: #166534;
  }

  .alert-danger {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #991b1b;
  }

  /* Utility Spacing */
  .mb-12 { margin-bottom: 0.75rem; }
  .mt-12 { margin-top: 0.75rem; }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
</style>
